<template>
  <Teleport to="body">
    <!-- ═══ ACTIVE WALKTHROUGH ═══ -->
    <transition name="walkthrough-fade">
      <div
        v-if="store.isActive && store.currentStep && !awaitingUserGeneration"
        v-show="!modalOpen"
        class="walkthrough-wrapper"
        :class="{
          'click-through-active': store.currentStep?.clickThrough,
          'interactive-active': store.currentStep?.interactive,
          'offtrack-active': isOffTrack
        }"
      >
        <!-- Backdrop overlay (click handler + SVG mask) -->
        <div class="walkthrough-overlay" @click.self="handleOverlayClick">
          <!-- Spotlight cutout (SVG mask) -->
          <svg
            v-if="spotlightRect && !isOffTrack"
            class="walkthrough-spotlight"
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              <mask id="walkthrough-mask">
                <rect width="100%" height="100%" fill="white" />
                <rect
                  :x="spotlightRect.x - 8"
                  :y="spotlightRect.y - 8"
                  :width="spotlightRect.width + 16"
                  :height="spotlightRect.height + 16"
                  rx="12"
                  ry="12"
                  fill="black"
                />
              </mask>
            </defs>
            <rect
              width="100%"
              height="100%"
              fill="rgba(0,0,0,0.6)"
              mask="url(#walkthrough-mask)"
            />
          </svg>

          <!-- Dark overlay when no spotlight target (center modals) -->
          <div
            v-if="!spotlightRect && !store.currentStep?.clickThrough"
            class="walkthrough-backdrop"
          />

          <!-- Click-through zone is handled via pointer-events:none on overlay -->

          <!-- Pulse ring on target -->
          <div
            v-if="spotlightRect && store.currentStep?.pulse"
            class="pulse-ring"
            :style="pulseStyle"
          />
        </div>
        <!-- ^^^ end .walkthrough-overlay (backdrop layer) -->

        <!-- Progress bar — now displayed in AppNavbarComponent instead -->

        <!-- Tooltip -->
        <transition name="tooltip-pop">
          <div
            v-show="!isOffTrack && tooltipReady"
            :key="store.currentStep?.id"
            class="walkthrough-tooltip"
            :class="[
              'placement-' + (store.currentStep?.placement || 'center'),
              { 'is-center': !spotlightRect },
              { 'is-compact': store.currentStep?.compact },
              { 'is-showcase': store.currentStep?.showcase }
            ]"
            :style="tooltipStyle"
          >
            <!-- ═══ SHOWCASE: Simulate ═══ -->
            <template v-if="store.currentStep?.showcase === 'simulate'">
              <div class="sc">
                <!-- Floating particles -->
                <div class="sc-particles">
                  <div
                    v-for="i in 12"
                    :key="i"
                    class="sc-particle"
                    :class="`sc-p-${i}`"
                  ></div>
                </div>

                <!-- Hero header -->
                <div
                  class="sc-hero"
                  :class="{ 'sc-visible': showcaseStep >= 1 }"
                >
                  <div class="sc-badge">
                    <span class="sc-badge-dot"></span>
                    POWERED BY AI
                  </div>
                  <h2 class="sc-hero-title">
                    See how
                    <span class="sc-gradient-text">MyUser</span>
                    turns shoppers into revenue
                  </h2>
                  <p class="sc-hero-sub">
                    Every customer action triggers intelligent, personalized
                    outreach — completely on autopilot.
                  </p>
                </div>

                <!-- ── Desktop: 3-column pipeline ── -->
                <div class="sc-pipeline">
                  <!-- STEP 1 -->
                  <div
                    class="sc-stage"
                    :class="{ 'sc-visible': showcaseStep >= 2 }"
                  >
                    <div class="sc-stage-num sc-num-blue">1</div>
                    <div class="sc-stage-card sc-card-blue">
                      <div class="sc-stage-icon">🛒</div>
                      <h3 class="sc-stage-title">Customer Acts</h3>
                      <p class="sc-stage-desc">
                        Every action is tracked in real-time
                      </p>
                      <ul class="sc-action-list">
                        <li>
                          <span class="sc-act-dot sc-dot-bg-blue"></span>
                          Browses products
                        </li>
                        <li>
                          <span class="sc-act-dot sc-dot-bg-amber"></span>
                          Adds items to cart
                        </li>
                        <li>
                          <span class="sc-act-dot sc-dot-bg-violet"></span>
                          Searches the store
                        </li>
                        <li>
                          <span class="sc-act-dot sc-dot-bg-green"></span>
                          Completes a purchase
                        </li>
                        <li>
                          <span class="sc-act-dot sc-dot-bg-red"></span>
                          Leaves without buying
                        </li>
                      </ul>
                    </div>
                  </div>

                  <!-- Connector 1 -->
                  <div
                    class="sc-connector"
                    :class="{ 'sc-visible': showcaseStep >= 3 }"
                  >
                    <span class="sc-chevron">›</span>
                  </div>

                  <!-- STEP 2 -->
                  <div
                    class="sc-stage"
                    :class="{ 'sc-visible': showcaseStep >= 3 }"
                  >
                    <div class="sc-stage-num sc-num-purple">2</div>
                    <div class="sc-stage-card sc-card-purple">
                      <div class="sc-brain">
                        <div class="sc-brain-ring"></div>
                        <div class="sc-brain-ring sc-ring-2"></div>
                        <span class="sc-brain-emoji">🧠</span>
                      </div>
                      <h3 class="sc-stage-title">AI Decides</h3>
                      <p class="sc-stage-desc">Adapts in real-time</p>
                      <ul class="sc-ai-list">
                        <li>
                          <span class="sc-ai-check">✓</span>
                          Generates personalized messages
                        </li>
                        <li>
                          <span class="sc-ai-check">✓</span>
                          Adjusts the plan as things change
                        </li>
                        <li>
                          <span class="sc-ai-check">✓</span>
                          Responds to incoming texts
                        </li>
                      </ul>
                    </div>
                  </div>

                  <!-- Connector 2 -->
                  <div
                    class="sc-connector"
                    :class="{ 'sc-visible': showcaseStep >= 4 }"
                  >
                    <span class="sc-chevron">›</span>
                  </div>

                  <!-- STEP 3 — Phone mockup messages -->
                  <div
                    class="sc-stage sc-stage-msgs"
                    :class="{ 'sc-visible': showcaseStep >= 4 }"
                  >
                    <div class="sc-stage-num sc-num-green">3</div>
                    <div class="sc-stage-card sc-card-green sc-phone-card">
                      <h3 class="sc-stage-title">Messages Sent</h3>
                      <p class="sc-stage-desc">
                        Personalized &amp; timed to convert
                      </p>

                      <!-- Email preview card -->
                      <div
                        class="sc-email-card"
                        :class="{ 'sc-visible': showcaseStep >= 5 }"
                      >
                        <div class="sc-email-topbar">
                          <span class="sc-email-icon">✉️</span>
                          <span class="sc-email-label">EMAIL</span>
                        </div>
                        <div class="sc-email-subject">
                          New arrivals that match your style ✨
                        </div>
                        <div class="sc-email-preview">
                          Hi Sarah, we picked these just for you based on your
                          recent browsing…
                        </div>
                        <div class="sc-email-cta">Shop Now →</div>
                      </div>

                      <!-- Phone: SMS -->
                      <div
                        class="sc-phone sc-phone-sms"
                        :class="{ 'sc-visible': showcaseStep >= 6 }"
                      >
                        <div class="sc-phone-header">
                          <span class="sc-phone-dot sc-dot-green"></span>
                          <span class="sc-phone-dot sc-dot-green"></span>
                          <span class="sc-phone-dot sc-dot-green"></span>
                        </div>
                        <div class="sc-phone-body">
                          <div class="sc-phone-label">
                            <span class="sc-phone-badge sc-badge-sms">
                              💬
                            </span>
                            SMS
                          </div>
                          <div class="sc-sms-bubble">
                            Hey! Your cart is waiting — 10%
                            off&nbsp;today&nbsp;🎉
                          </div>
                          <div class="sc-sms-meta">Delivered · just now</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- ── Mobile: Vertical story ── -->
                <div class="sc-mobile-story">
                  <!-- Step 1 -->
                  <div
                    class="sc-m-step"
                    :class="{ 'sc-visible': showcaseStep >= 2 }"
                  >
                    <div class="sc-m-line">
                      <div class="sc-m-dot sc-dot-blue"></div>
                    </div>
                    <div class="sc-m-card sc-m-blue">
                      <div class="sc-m-top">
                        <span class="sc-m-num">1</span>
                        <span class="sc-m-icon">🛒</span>
                        <span class="sc-m-title">Customer Acts</span>
                      </div>
                      <div class="sc-m-chips">
                        <span
                          ><span
                            class="sc-act-dot"
                            style="background: #3b82f6"
                          ></span>
                          Views product</span
                        >
                        <span
                          ><span
                            class="sc-act-dot"
                            style="background: #f59e0b"
                          ></span>
                          Adds to cart</span
                        >
                        <span
                          ><span
                            class="sc-act-dot"
                            style="background: #8b5cf6"
                          ></span>
                          Searches</span
                        >
                        <span
                          ><span
                            class="sc-act-dot"
                            style="background: #10b981"
                          ></span>
                          Purchases</span
                        >
                        <span
                          ><span
                            class="sc-act-dot"
                            style="background: #ef4444"
                          ></span>
                          Abandons</span
                        >
                      </div>
                    </div>
                  </div>

                  <!-- Step 2 -->
                  <div
                    class="sc-m-step"
                    :class="{ 'sc-visible': showcaseStep >= 3 }"
                  >
                    <div class="sc-m-line">
                      <div class="sc-m-dot sc-dot-purple"></div>
                    </div>
                    <div class="sc-m-card sc-m-purple">
                      <div class="sc-m-top">
                        <span class="sc-m-num">2</span>
                        <span class="sc-m-icon">🧠</span>
                        <span class="sc-m-title">AI Decides</span>
                      </div>
                      <p class="sc-m-desc">
                        Picks the best template, writes copy, and optimizes send
                        time
                      </p>
                    </div>
                  </div>

                  <!-- Step 3 -->
                  <div
                    class="sc-m-step"
                    :class="{ 'sc-visible': showcaseStep >= 4 }"
                  >
                    <div class="sc-m-line sc-m-line-last">
                      <div class="sc-m-dot sc-dot-green"></div>
                    </div>
                    <div class="sc-m-card sc-m-green">
                      <div class="sc-m-top">
                        <span class="sc-m-num">3</span>
                        <span class="sc-m-icon">📨</span>
                        <span class="sc-m-title">Messages Sent</span>
                      </div>

                      <!-- Mini text bubbles on mobile -->
                      <div class="sc-m-bubbles">
                        <div
                          class="sc-m-bub sc-m-bub-email"
                          :class="{ 'sc-visible': showcaseStep >= 5 }"
                        >
                          <span class="sc-m-bub-icon">✉️</span>
                          <div class="sc-m-bub-body">
                            <strong>Email</strong>
                            <span> New arrivals that match your style ✨ </span>
                            <span class="sc-m-cta-pill">Shop Now →</span>
                          </div>
                        </div>
                        <div
                          class="sc-m-bub sc-m-bub-sms"
                          :class="{ 'sc-visible': showcaseStep >= 6 }"
                        >
                          <span class="sc-m-bub-icon">💬</span>
                          <div class="sc-m-bub-body">
                            <strong>SMS</strong>
                            <span>
                              Your cart is waiting — 10% off today 🎉
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>

            <!-- ═══ SHOWCASE: Integrations ═══ -->
            <template
              v-else-if="store.currentStep?.showcase === 'integrations'"
            >
              <div class="ig-showcase">
                <!-- Hero -->
                <div
                  class="ig-hero"
                  :class="{ 'sc-visible': showcaseStep >= 1 }"
                >
                  <h2 class="ig-title">
                    Emails &amp; texts are
                    <span class="sc-gradient-text">ready to go</span>
                  </h2>
                  <p class="ig-subtitle">
                    We handle delivery for you automatically. You can optionally
                    connect your own accounts for more control.
                  </p>
                </div>

                <!-- Two service rows -->
                <div class="ig-services">
                  <!-- SendGrid -->
                  <div
                    class="ig-service"
                    :class="{ 'sc-visible': showcaseStep >= 2 }"
                  >
                    <div class="ig-service-logo">
                      <img
                        src="@/assets/logos/sengrid.png"
                        alt="SendGrid"
                        class="ig-logo-img"
                      />
                    </div>
                    <div class="ig-service-info">
                      <h3 class="ig-service-name">SendGrid</h3>
                      <p class="ig-service-desc">
                        Send emails from your own domain instead of &ldquo;via
                        MyUser&rdquo;
                      </p>
                    </div>
                  </div>

                  <!-- Twilio -->
                  <div
                    class="ig-service"
                    :class="{ 'sc-visible': showcaseStep >= 3 }"
                  >
                    <div class="ig-service-logo">
                      <img
                        src="@/assets/logos/twilio.png"
                        alt="Twilio"
                        class="ig-logo-img"
                      />
                    </div>
                    <div class="ig-service-info">
                      <h3 class="ig-service-name">Twilio</h3>
                      <p class="ig-service-desc">
                        Send texts from your own phone number instead of a
                        shared one
                      </p>
                    </div>
                  </div>
                </div>

                <!-- Reassurance -->
                <div
                  class="ig-note"
                  :class="{ 'sc-visible': showcaseStep >= 4 }"
                >
                  Neither is required — everything works without them.
                </div>
              </div>
            </template>

            <!-- ═══ SHOWCASE: Forms ═══ -->
            <template v-else-if="store.currentStep?.showcase === 'forms'">
              <div class="lf-showcase">
                <!-- Hero -->
                <div
                  class="lf-hero"
                  :class="{ 'sc-visible': showcaseStep >= 1 }"
                >
                  <div class="lf-badge">
                    <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M8 1l1.8 5.4H15l-4.2 3.1 1.6 5-4.4-3.2L3.6 14.5l1.6-5L1 6.4h5.2z"
                        fill="currentColor"
                      />
                    </svg>
                    Lead Capture
                  </div>
                  <h2 class="lf-title">
                    Turn every visitor into a
                    <span class="sc-gradient-text">known customer</span>
                  </h2>
                  <p class="lf-subtitle">
                    AI-designed forms appear at the perfect moment on your
                    storefront — every signup auto-linked to browsing behavior.
                  </p>
                </div>

                <!-- Type switcher tabs -->
                <div
                  class="lf-tabs"
                  :class="{ 'sc-visible': showcaseStep >= 2 }"
                >
                  <button
                    class="lf-tab"
                    :class="{ 'lf-tab-active': lfActiveType === 'popup' }"
                    @click="pickLfType('popup')"
                  >
                    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
                      <rect
                        x="1"
                        y="2"
                        width="14"
                        height="12"
                        rx="2"
                        stroke="currentColor"
                        stroke-width="1.3"
                      />
                      <rect
                        x="4"
                        y="5"
                        width="8"
                        height="6"
                        rx="1.5"
                        fill="currentColor"
                        opacity="0.12"
                      />
                      <rect
                        x="4"
                        y="5"
                        width="8"
                        height="6"
                        rx="1.5"
                        stroke="currentColor"
                        stroke-width="1.1"
                      />
                    </svg>
                    Popup
                  </button>
                  <button
                    class="lf-tab"
                    :class="{ 'lf-tab-active': lfActiveType === 'slidein' }"
                    @click="pickLfType('slidein')"
                  >
                    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
                      <rect
                        x="1"
                        y="2"
                        width="14"
                        height="12"
                        rx="2"
                        stroke="currentColor"
                        stroke-width="1.3"
                      />
                      <rect
                        x="8"
                        y="7"
                        width="6"
                        height="6"
                        rx="1.5"
                        fill="currentColor"
                        opacity="0.12"
                      />
                      <rect
                        x="8"
                        y="7"
                        width="6"
                        height="6"
                        rx="1.5"
                        stroke="currentColor"
                        stroke-width="1.1"
                      />
                    </svg>
                    Slide-in
                  </button>
                  <button
                    class="lf-tab"
                    :class="{ 'lf-tab-active': lfActiveType === 'banner' }"
                    @click="pickLfType('banner')"
                  >
                    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
                      <rect
                        x="1"
                        y="2"
                        width="14"
                        height="12"
                        rx="2"
                        stroke="currentColor"
                        stroke-width="1.3"
                      />
                      <rect
                        x="2.5"
                        y="3.5"
                        width="11"
                        height="3"
                        rx="1"
                        fill="currentColor"
                        opacity="0.12"
                      />
                      <rect
                        x="2.5"
                        y="3.5"
                        width="11"
                        height="3"
                        rx="1"
                        stroke="currentColor"
                        stroke-width="1.1"
                      />
                    </svg>
                    Banner
                  </button>
                </div>

                <!-- Interactive browser mockup -->
                <div
                  class="lf-stage"
                  :class="{ 'sc-visible': showcaseStep >= 2 }"
                >
                  <div class="lf-browser">
                    <div class="lf-browser-bar">
                      <span class="lf-dot lf-dot-r"></span>
                      <span class="lf-dot lf-dot-y"></span>
                      <span class="lf-dot lf-dot-g"></span>
                      <div class="lf-url-pill">
                        <svg
                          width="9"
                          height="9"
                          viewBox="0 0 16 16"
                          fill="none"
                        >
                          <rect
                            x="3"
                            y="6"
                            width="10"
                            height="7"
                            rx="1.5"
                            stroke="#94a3b8"
                            stroke-width="1.3"
                          />
                          <path
                            d="M5 6V5a3 3 0 016 0v1"
                            stroke="#94a3b8"
                            stroke-width="1.3"
                            stroke-linecap="round"
                          />
                        </svg>
                        yourstore.com
                      </div>
                      <div class="lf-bar-icons">
                        <svg
                          width="11"
                          height="11"
                          viewBox="0 0 16 16"
                          fill="none"
                        >
                          <circle
                            cx="7"
                            cy="7"
                            r="4.5"
                            stroke="#94a3b8"
                            stroke-width="1.3"
                          />
                          <path
                            d="M10.5 10.5L14 14"
                            stroke="#94a3b8"
                            stroke-width="1.3"
                            stroke-linecap="round"
                          />
                        </svg>
                        <svg
                          width="11"
                          height="11"
                          viewBox="0 0 16 16"
                          fill="none"
                        >
                          <path
                            d="M2.5 4h11l-1.2 8.5a1.5 1.5 0 01-1.5 1.3H5.2a1.5 1.5 0 01-1.5-1.3z"
                            stroke="#94a3b8"
                            stroke-width="1.2"
                          />
                          <path
                            d="M5.5 4V2.5a.5.5 0 01.5-.5h4a.5.5 0 01.5.5V4"
                            stroke="#94a3b8"
                            stroke-width="1.2"
                          />
                        </svg>
                      </div>
                    </div>

                    <div class="lf-viewport">
                      <!-- Storefront skeleton -->
                      <div class="lf-sf-nav">
                        <div class="lf-sf-logo"></div>
                        <div class="lf-sf-menu">
                          <span></span><span></span><span></span>
                        </div>
                      </div>
                      <div class="lf-sf-hero-banner">
                        <div class="lf-sf-hero-text"></div>
                        <div class="lf-sf-hero-sub"></div>
                        <div class="lf-sf-hero-btn"></div>
                      </div>
                      <div class="lf-sf-section-label"></div>
                      <div class="lf-sf-grid">
                        <div class="lf-sf-card">
                          <div class="lf-sf-img lf-sf-img-1"></div>
                          <div class="lf-sf-line"></div>
                          <div class="lf-sf-price"></div>
                        </div>
                        <div class="lf-sf-card">
                          <div class="lf-sf-img lf-sf-img-2"></div>
                          <div class="lf-sf-line"></div>
                          <div class="lf-sf-price"></div>
                        </div>
                        <div class="lf-sf-card">
                          <div class="lf-sf-img lf-sf-img-3"></div>
                          <div class="lf-sf-line"></div>
                          <div class="lf-sf-price"></div>
                        </div>
                        <div class="lf-sf-card lf-sf-card-hide">
                          <div class="lf-sf-img lf-sf-img-4"></div>
                          <div class="lf-sf-line"></div>
                          <div class="lf-sf-price"></div>
                        </div>
                      </div>

                      <!-- Dimmer when form visible -->
                      <div
                        class="lf-dimmer"
                        :class="{ 'lf-dim-on': showcaseStep >= 3 }"
                      ></div>

                      <!-- POPUP form (centered) -->
                      <Transition name="lf-morph">
                        <div
                          v-if="lfActiveType === 'popup'"
                          key="popup"
                          class="lf-mock lf-mock-popup"
                          :class="{ 'sc-visible': showcaseStep >= 3 }"
                        >
                          <div
                            class="lf-mock-accent lf-mock-accent-purple"
                          ></div>
                          <div class="lf-mock-x">✕</div>
                          <div class="lf-mock-body">
                            <div class="lf-mock-icon">🎁</div>
                            <div class="lf-mock-h">Get 10% off</div>
                            <div class="lf-mock-p">
                              Sign up for exclusive deals
                            </div>
                            <div class="lf-mock-field">
                              <svg
                                class="lf-field-icon"
                                width="10"
                                height="10"
                                viewBox="0 0 16 16"
                                fill="none"
                              >
                                <rect
                                  x="1"
                                  y="3"
                                  width="14"
                                  height="10"
                                  rx="2"
                                  stroke="#94a3b8"
                                  stroke-width="1.3"
                                />
                                <path
                                  d="M1 5l7 4.5L15 5"
                                  stroke="#94a3b8"
                                  stroke-width="1.3"
                                />
                              </svg>
                              <span>Email address</span>
                            </div>
                            <div class="lf-mock-cta">Claim Discount</div>
                          </div>
                        </div>
                      </Transition>

                      <!-- SLIDE-IN form (bottom right) -->
                      <Transition name="lf-morph">
                        <div
                          v-if="lfActiveType === 'slidein'"
                          key="slidein"
                          class="lf-mock lf-mock-slide"
                          :class="{ 'sc-visible': showcaseStep >= 3 }"
                        >
                          <div class="lf-slide-header">
                            <div class="lf-slide-avatar">💬</div>
                            <div class="lf-slide-info">
                              <div class="lf-mock-h lf-mock-h-sm">
                                Quick question
                              </div>
                              <div class="lf-slide-status">
                                <span class="lf-online-dot"></span> Online now
                              </div>
                            </div>
                            <div class="lf-mock-x lf-mock-x-slide">✕</div>
                          </div>
                          <div class="lf-slide-body">
                            <div class="lf-slide-bubble">
                              Want 15% off your first order? 🎉
                            </div>
                            <div class="lf-mock-field">
                              <svg
                                class="lf-field-icon"
                                width="10"
                                height="10"
                                viewBox="0 0 16 16"
                                fill="none"
                              >
                                <rect
                                  x="1"
                                  y="3"
                                  width="14"
                                  height="10"
                                  rx="2"
                                  stroke="#94a3b8"
                                  stroke-width="1.3"
                                />
                                <path
                                  d="M1 5l7 4.5L15 5"
                                  stroke="#94a3b8"
                                  stroke-width="1.3"
                                />
                              </svg>
                              <span>Enter your email</span>
                            </div>
                            <div class="lf-mock-cta lf-mock-cta-green">
                              Yes please!
                            </div>
                          </div>
                        </div>
                      </Transition>

                      <!-- BANNER form (top) -->
                      <Transition name="lf-morph">
                        <div
                          v-if="lfActiveType === 'banner'"
                          key="banner"
                          class="lf-mock lf-mock-banner"
                          :class="{ 'sc-visible': showcaseStep >= 3 }"
                        >
                          <div class="lf-banner-left">
                            <span class="lf-banner-emoji">🔥</span>
                            <strong>Free shipping today!</strong>
                            <span class="lf-banner-sub"
                              >Enter email to unlock</span
                            >
                          </div>
                          <div class="lf-banner-right">
                            <div class="lf-mock-field lf-mock-field-inline">
                              <span>Email</span>
                            </div>
                            <div class="lf-mock-cta lf-mock-cta-dark">Go</div>
                          </div>
                        </div>
                      </Transition>
                    </div>
                  </div>
                </div>

                <!-- Bottom feature chips -->
                <div
                  class="lf-features"
                  :class="{ 'sc-visible': showcaseStep >= 4 }"
                >
                  <div class="lf-chip">
                    <span class="lf-chip-dot lf-chip-dot-blue"></span>
                    Linked to browsing data
                  </div>
                  <div class="lf-chip">
                    <span class="lf-chip-dot lf-chip-dot-purple"></span>
                    Auto discount codes
                  </div>
                  <div class="lf-chip">
                    <span class="lf-chip-dot lf-chip-dot-green"></span>
                    AI-generated instantly
                  </div>
                </div>
              </div>
            </template>

            <!-- ═══ SHOWCASE: Forms Picker ═══ -->
            <template v-else-if="store.currentStep?.showcase === 'forms-pick'">
              <div class="fp-showcase">
                <!-- Floating particles -->
                <div class="sc-particles">
                  <div
                    v-for="i in 12"
                    :key="i"
                    class="sc-particle"
                    :class="`sc-p-${i}`"
                  ></div>
                </div>

                <!-- Hero -->
                <div
                  class="fp-hero"
                  :class="{ 'sc-visible': showcaseStep >= 1 }"
                >
                  <div class="sc-badge">
                    <span class="sc-badge-dot"></span>
                    AI GENERATED
                  </div>
                  <h2 class="fp-title">
                    We made you some
                    <span class="sc-gradient-text">starter forms</span>
                  </h2>
                  <p class="fp-subtitle">
                    AI designed 2 options per type to get you going. Preview
                    them on your store and pick your favorites — you can always
                    create new ones or edit these later.
                  </p>
                </div>

                <!-- A / B comparison visual -->
                <div
                  class="fp-compare"
                  :class="{ 'sc-visible': showcaseStep >= 2 }"
                >
                  <div class="fp-compare-inner">
                    <!-- Option A side -->
                    <div class="fp-side fp-side-a">
                      <div class="fp-side-badge">A</div>
                      <div class="fp-mini-form fp-mini-a">
                        <div class="fp-mini-accent"></div>
                        <div class="fp-mini-heading"></div>
                        <div class="fp-mini-sub"></div>
                        <div class="fp-mini-field"></div>
                        <div class="fp-mini-btn"></div>
                      </div>
                    </div>

                    <!-- VS divider -->
                    <div class="fp-vs">
                      <div class="fp-vs-ring">
                        <span>VS</span>
                      </div>
                      <div class="fp-vs-line"></div>
                    </div>

                    <!-- Option B side -->
                    <div class="fp-side fp-side-b">
                      <div class="fp-side-badge fp-side-badge-b">B</div>
                      <div class="fp-mini-form fp-mini-b">
                        <div class="fp-mini-accent fp-mini-accent-dark"></div>
                        <div class="fp-mini-heading fp-mini-h-dark"></div>
                        <div class="fp-mini-sub fp-mini-s-dark"></div>
                        <div class="fp-mini-field fp-mini-f-dark"></div>
                        <div class="fp-mini-btn fp-mini-btn-warm"></div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Type cards row -->
                <div
                  class="fp-types"
                  :class="{ 'sc-visible': showcaseStep >= 3 }"
                >
                  <div class="fp-type-card fp-type-popup">
                    <div class="fp-type-glow"></div>
                    <div class="fp-type-icon">
                      <svg
                        width="22"
                        height="22"
                        viewBox="0 0 16 16"
                        fill="none"
                      >
                        <rect
                          x="1"
                          y="2"
                          width="14"
                          height="12"
                          rx="2"
                          stroke="currentColor"
                          stroke-width="1.3"
                        />
                        <rect
                          x="4"
                          y="5"
                          width="8"
                          height="6"
                          rx="1.5"
                          stroke="currentColor"
                          stroke-width="1.1"
                          opacity="0.6"
                        />
                      </svg>
                    </div>
                    <div class="fp-type-name">Popup</div>
                    <div class="fp-type-ab">
                      <span class="fp-ab fp-ab-a">A</span>
                      <span class="fp-ab fp-ab-b">B</span>
                    </div>
                  </div>
                  <div class="fp-type-card fp-type-slide">
                    <div class="fp-type-glow"></div>
                    <div class="fp-type-icon">
                      <svg
                        width="22"
                        height="22"
                        viewBox="0 0 16 16"
                        fill="none"
                      >
                        <rect
                          x="1"
                          y="2"
                          width="14"
                          height="12"
                          rx="2"
                          stroke="currentColor"
                          stroke-width="1.3"
                        />
                        <rect
                          x="8"
                          y="7"
                          width="6"
                          height="6"
                          rx="1.5"
                          stroke="currentColor"
                          stroke-width="1.1"
                          opacity="0.6"
                        />
                      </svg>
                    </div>
                    <div class="fp-type-name">Slide-in</div>
                    <div class="fp-type-ab">
                      <span class="fp-ab fp-ab-a">A</span>
                      <span class="fp-ab fp-ab-b">B</span>
                    </div>
                  </div>
                  <div class="fp-type-card fp-type-banner">
                    <div class="fp-type-glow"></div>
                    <div class="fp-type-icon">
                      <svg
                        width="22"
                        height="22"
                        viewBox="0 0 16 16"
                        fill="none"
                      >
                        <rect
                          x="1"
                          y="2"
                          width="14"
                          height="12"
                          rx="2"
                          stroke="currentColor"
                          stroke-width="1.3"
                        />
                        <rect
                          x="2.5"
                          y="3.5"
                          width="11"
                          height="3"
                          rx="1"
                          stroke="currentColor"
                          stroke-width="1.1"
                          opacity="0.6"
                        />
                      </svg>
                    </div>
                    <div class="fp-type-name">Banner</div>
                    <div class="fp-type-ab">
                      <span class="fp-ab fp-ab-a">A</span>
                      <span class="fp-ab fp-ab-b">B</span>
                    </div>
                  </div>
                </div>

                <!-- How it works stepper -->
                <div
                  class="fp-flow"
                  :class="{ 'sc-visible': showcaseStep >= 4 }"
                >
                  <div class="fp-flow-step">
                    <div class="fp-flow-num">1</div>
                    <div class="fp-flow-text">
                      <strong>Open your store</strong>
                      <span>See forms live on your site</span>
                    </div>
                  </div>
                  <div class="fp-flow-connector">
                    <svg width="20" height="12" viewBox="0 0 20 12">
                      <path
                        d="M0 6h16M12 1l5 5-5 5"
                        stroke="#c4b5fd"
                        stroke-width="1.5"
                        fill="none"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </div>
                  <div class="fp-flow-step">
                    <div class="fp-flow-num fp-flow-num-2">2</div>
                    <div class="fp-flow-text">
                      <strong>Compare A &amp; B</strong>
                      <span>Toggle between options</span>
                    </div>
                  </div>
                  <div class="fp-flow-connector">
                    <svg width="20" height="12" viewBox="0 0 20 12">
                      <path
                        d="M0 6h16M12 1l5 5-5 5"
                        stroke="#c4b5fd"
                        stroke-width="1.5"
                        fill="none"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </div>
                  <div class="fp-flow-step">
                    <div class="fp-flow-num fp-flow-num-3">3</div>
                    <div class="fp-flow-text">
                      <strong>Pick your fav</strong>
                      <span>One click and you're done</span>
                    </div>
                  </div>
                </div>

                <!-- Loading indicator -->
                <div
                  v-if="walkthroughFormsLoading"
                  class="fp-loading"
                  :class="{ 'sc-visible': showcaseStep >= 1 }"
                >
                  <div class="lf-gen-spinner"></div>
                  <span
                    >AI is designing your form options… this takes a
                    moment</span
                  >
                </div>

                <!-- Skip option -->
                <div
                  class="fp-skip-section"
                  :class="{ 'sc-visible': showcaseStep >= 4 }"
                >
                  <button class="fp-skip-btn" @click="store.next()">
                    I'll do this later
                  </button>
                  <p class="fp-skip-hint">
                    You can pick forms anytime from this page
                  </p>
                </div>
              </div>
            </template>

            <!-- ═══ SHOWCASE: Welcome ═══ -->
            <template v-else-if="store.currentStep?.showcase === 'welcome'">
              <div class="welcome-showcase">
                <!-- Animated check ring -->
                <div class="wc-check-wrap">
                  <svg class="wc-check-svg" viewBox="0 0 64 64">
                    <circle class="wc-check-bg" cx="32" cy="32" r="29" />
                    <circle class="wc-check-ring" cx="32" cy="32" r="29" />
                    <path class="wc-check-tick" d="M20 32l8 8 16-16" />
                  </svg>
                </div>

                <!-- Demo mode -->
                <template v-if="demoStore.isDemoMode">
                  <div class="wc-demo-badge">INTERACTIVE DEMO</div>
                  <h2 class="wc-title">
                    See
                    <span class="wc-title-em">MyUser AI</span>
                    in action
                  </h2>
                  <p class="wc-subtitle">
                    Walk through a real store with sample data. You'll simulate
                    customer behavior and watch the AI respond live.
                  </p>
                </template>

                <!-- Real user mode -->
                <template v-else>
                  <h2 class="wc-title">
                    Your AI marketing engine
                    <span class="wc-title-em">is ready</span>
                  </h2>
                  <p class="wc-subtitle">
                    See how customer actions become personalized messages
                    —&nbsp;automatically.
                  </p>
                </template>

                <!-- Pipeline -->
                <div class="wc-pipeline">
                  <div class="wc-step wc-s1">
                    <div class="wc-step-dot wc-dot-blue"></div>
                    <div class="wc-step-content">
                      <span class="wc-step-emoji">🛒</span>
                      <span class="wc-step-name">Customer acts</span>
                    </div>
                  </div>
                  <div class="wc-connector wc-c1">
                    <div class="wc-conn-line"></div>
                    <div class="wc-conn-pulse"></div>
                  </div>
                  <div class="wc-step wc-s2">
                    <div class="wc-step-dot wc-dot-purple"></div>
                    <div class="wc-step-content">
                      <span class="wc-step-emoji">🧠</span>
                      <span class="wc-step-name">AI decides</span>
                    </div>
                  </div>
                  <div class="wc-connector wc-c2">
                    <div class="wc-conn-line"></div>
                    <div class="wc-conn-pulse"></div>
                  </div>
                  <div class="wc-step wc-s3">
                    <div class="wc-step-dot wc-dot-green"></div>
                    <div class="wc-step-content">
                      <span class="wc-step-emoji">✉️</span>
                      <span class="wc-step-name">Message sent</span>
                    </div>
                  </div>
                </div>

                <!-- Footer meta -->
                <div class="wc-meta">
                  <span class="wc-meta-pill">⏱ ~1 min</span>
                  <span class="wc-meta-pill">12 interactive steps</span>
                  <span v-if="demoStore.isDemoMode" class="wc-meta-pill"
                    >🧪 Sample data</span
                  >
                </div>
              </div>
            </template>

            <!-- ═══ SHOWCASE: Interactive Simulate ═══ -->
            <template
              v-else-if="store.currentStep?.showcase === 'simulate-interactive'"
            >
              <WalkthroughSimulate ref="simRef" />
            </template>

            <!-- ═══ REGULAR TOOLTIP ═══ -->
            <template v-else>
              <!-- Step icon -->
              <div class="tooltip-icon">{{ store.currentStep?.icon }}</div>

              <!-- Content -->
              <div class="tooltip-content">
                <h3 class="tooltip-title">{{ store.currentStep?.title }}</h3>
                <p class="tooltip-body">
                  {{ tooltipBodyOverride || store.currentStep?.body }}
                </p>

                <!-- Drag objective for message frequency step -->
                <div
                  v-if="store.currentStep?.id === 'dashboard-message-frequency'"
                  class="drag-objective"
                  :class="{ 'objective-complete': rangeDragged }"
                >
                  <span class="objective-check">{{
                    rangeDragged ? '✅' : '⬜'
                  }}</span>
                  <span class="objective-text">{{
                    rangeDragged
                      ? 'Range adjusted!'
                      : 'Try dragging the green band'
                  }}</span>
                </div>
              </div>
            </template>

            <!-- Actions -->
            <div class="tooltip-actions">
              <button
                class="tooltip-btn tooltip-btn-skip"
                @click="store.dismiss()"
              >
                Skip tour
              </button>
              <button
                v-if="store.currentStepIndex > 0"
                class="tooltip-btn tooltip-btn-back"
                @click="simIsInSubView ? simBackToMenu() : store.prev()"
              >
                ← <kbd class="key-hint">←</kbd>
              </button>
              <button
                class="tooltip-btn tooltip-btn-next"
                @click="simStepNeedsMore ? simBackToMenu() : handleNext()"
              >
                {{ simActionLabel }}
                <kbd v-if="!simStepNeedsMore" class="key-hint">→</kbd>
              </button>
            </div>

            <!-- Step counter -->
            <div class="tooltip-counter">
              {{ store.visibleStepNumber }} / {{ store.totalSteps }}
            </div>
          </div>
        </transition>
      </div>
    </transition>

    <!-- ═══ COMPLETION CELEBRATION ═══ -->
    <transition name="celebrate-fade">
      <div
        v-if="showCelebration"
        class="celebration-overlay"
        @click.self="closeCelebration"
      >
        <!-- Confetti particles -->
        <div class="confetti-container">
          <div
            v-for="i in 50"
            :key="i"
            class="confetti"
            :style="confettiStyle(i)"
          />
        </div>

        <div
          class="celebration-card"
          :class="{
            'celebration-card-demo-shell': demoStore.isDemoMode,
            'celebration-card-immersive': !demoStore.isDemoMode
          }"
        >
          <template v-if="demoStore.isDemoMode">
            <DemoCelebrationV3
              @install="goToShopifyInstall"
              @explore="closeCelebration"
              @replay="replayTour"
            />
          </template>

          <WalkthroughCelebration
            v-else
            :loading="celebrationLoading"
            :is-active="projectIsActive"
            :activating="activatingProject"
            :activated="activationSuccess"
            @close="closeCelebration"
            @activate="activateProject"
            @replay="replayTour"
          />
        </div>
      </div>
    </transition>

    <!-- ═══ OFF-TRACK RETURN PILL ═══ -->
    <transition name="offtrack-slide">
      <div v-if="store.isActive && isOffTrack" class="offtrack-pill">
        <span class="offtrack-icon">📍</span>
        <span class="offtrack-text">Tour paused — you navigated away</span>
        <button class="offtrack-btn" @click="returnToTour">
          Back to {{ offTrackPageName }} →
        </button>
        <button
          class="offtrack-dismiss"
          @click="store.dismiss()"
          title="End tour"
        >
          ✕
        </button>
      </div>
    </transition>

    <!-- ═══ GENERATING USER OVERLAY ═══ -->
    <transition name="walkthrough-fade">
      <div v-if="awaitingUserGeneration" class="wt-generating-overlay">
        <div class="wt-generating-card">
          <div class="wt-generating-spinner"></div>
          <h3 class="wt-generating-title">Setting up your tour</h3>
          <p class="wt-generating-body">
            We're creating a sample customer so you can see how everything
            works. Just a moment…
          </p>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useWalkthroughStore } from '@/store/walkthroughStore';
import { useDemoStore } from '@/store/demoStore';
import { useAccessStore } from '@/store/accessStore';
import {
  streamWalkthroughAI,
  type WalkthroughAISummary,
  type WalkthroughAIMessageChange
} from '@/api/messages';
import WalkthroughSimulate from '@/components/WalkthroughSimulate.vue';
import DemoCelebrationV3 from '@/components/DemoCelebrationV3.vue';
import WalkthroughCelebration from '@/components/WalkthroughCelebration.vue';
import { getProject, setActiveStatus, updateProject } from '@/api/projects';
import {
  aiGenerateForm,
  aiGenerateWalkthroughForms,
  resetWalkthroughForms,
  getLeadForms,
  deleteLeadForm,
  updateLeadForm
} from '@/api/forms';
import type { WalkthroughFormPair } from '@/api/forms';

const store = useWalkthroughStore();
const demoStore = useDemoStore();
const accessStore = useAccessStore();
const router = useRouter();
const route = useRoute();

const spotlightRect = ref<DOMRect | null>(null);
const pinnedTooltipTop = ref<number | null>(null); // Lock tooltip vertical pos once placed
const tooltipReady = ref(true);
const modalOpen = ref(false); // True when a page modal (.modal-overlay) is open
let modalObserver: MutationObserver | null = null;
const showCelebration = ref(false);
const showcaseStep = ref(0);
let showcaseTimer: ReturnType<typeof setInterval> | null = null;
const lfActiveType = ref<'popup' | 'slidein' | 'banner'>('popup');
let lfCycleTimer: ReturnType<typeof setInterval> | null = null;
const simRef = ref<InstanceType<typeof WalkthroughSimulate> | null>(null);

// ── Stop walkthrough if user loses access ──
watch(
  () => accessStore.showNoAccess,
  (denied) => {
    if (denied && store.isActive) {
      console.log('[walkthrough] Access denied — dismissing walkthrough');
      store.dismiss();
    }
  }
);

// ── Celebration: project active status (non-demo) ──
const projectIsActive = ref<boolean | null>(null);
const activatingProject = ref(false);
const activationSuccess = ref(false);
const celebrationLoading = ref(false);
const celebrationStage = ref(0);
let celebrationTimer: ReturnType<typeof setTimeout>[] = [];

function startCelebrationAnimation() {
  stopCelebrationAnimation();
  celebrationStage.value = 0;
  const delays = [100, 500, 950, 1400, 1850];
  delays.forEach((ms, i) => {
    const t = setTimeout(() => {
      celebrationStage.value = i + 1;
    }, ms);
    celebrationTimer.push(t);
  });
}

function stopCelebrationAnimation() {
  celebrationTimer.forEach(clearTimeout);
  celebrationTimer = [];
  celebrationStage.value = 0;
}

// ── Cached walkthrough AI results (from SSE stream) ──
const cachedSummaries = ref<WalkthroughAISummary[] | null>(null);
const cachedPreviousInsight = ref<string | null>(null);

// ── Walkthrough forms picker ──
const walkthroughFormsLoading = ref(false);
const walkthroughFormPairs = ref<WalkthroughFormPair[]>([]);
const walkthroughShopName = ref<string | null>(null);
let walkthroughPickerWindow: Window | null = null;
let formsPrefetchPromise: Promise<void> | null = null;

/** Prefetch walkthrough forms + shopName in the background.
 *  Returns a promise that resolves when both are loaded.
 *  Safe to call multiple times — subsequent calls return the same promise. */
function prefetchWalkthroughForms(): Promise<void> {
  if (formsPrefetchPromise) return formsPrefetchPromise;
  if (demoStore.isDemoMode || !store.projectId) return Promise.resolve();
  if (store.walkthroughFormsPicked) return Promise.resolve();

  const projectId = store.projectId;
  if (!projectId) return Promise.resolve();

  formsPrefetchPromise = (async () => {
    // Fetch shopName + sync backend flags
    if (!walkthroughShopName.value) {
      try {
        const project = await getProject(projectId);
        walkthroughShopName.value = project.shopName || null;
        if (
          (project as unknown as Record<string, unknown>)
            .walkthroughFormsGenerated
        ) {
          store.walkthroughFormsGenerated = true;
        }
        if (
          (project as unknown as Record<string, unknown>).walkthroughFormsPicked
        ) {
          store.walkthroughFormsPicked = true;
          store.persistState();
        }
      } catch {
        console.warn(
          '[walkthrough] Prefetch: could not fetch project for shopName'
        );
      }
    }

    // Fetch walkthrough form pairs
    if (!walkthroughFormPairs.value.length) {
      if (!store.walkthroughFormsGenerated) {
        walkthroughFormsLoading.value = true;
      }
      try {
        console.log('[walkthrough] Prefetching walkthrough form pairs…');
        const resp = await aiGenerateWalkthroughForms(projectId);
        if (resp.success) {
          walkthroughFormPairs.value = resp.pairs;
          store.walkthroughFormsGenerated = true;
          store.persistState();
          console.log(
            '[walkthrough] Walkthrough forms prefetched:',
            resp.forms.length
          );
        } else {
          console.warn(
            '[walkthrough] Walkthrough forms had errors:',
            resp.errors
          );
        }
      } catch (err) {
        console.warn(
          '[walkthrough] Failed to prefetch walkthrough forms:',
          err
        );
      } finally {
        walkthroughFormsLoading.value = false;
      }
    }

    // Also generate the sandbox form if needed
    if (!store.sandboxFormGenerated && projectId) {
      try {
        console.log('[walkthrough] Generating sandbox lead form…');
        await aiGenerateForm(
          projectId,
          'Create a stylish newsletter signup popup form. Include a catchy headline, a short description about exclusive deals and updates, an email field, and a submit button. Use a warm, inviting tone.'
        );
        store.sandboxFormGenerated = true;
        store.persistState();
        console.log('[walkthrough] Sandbox form generated & saved');
      } catch (err) {
        console.warn('[walkthrough] Failed to generate sandbox form:', err);
      }
    }
  })();
  return formsPrefetchPromise;
}

/** Open the user's store with the walkthrough picker mode.
 *  Must be synchronous (no await before window.open) to avoid popup blockers. */
function openWalkthroughPicker() {
  if (!store.projectId) return;

  if (!walkthroughShopName.value) {
    console.warn('[walkthrough] No shopName — cannot open picker');
    return;
  }

  // Collect form IDs from pairs to pass as URL param
  const formIds: string[] = [];
  for (const pair of walkthroughFormPairs.value) {
    if (pair.a) formIds.push(pair.a.id);
    if (pair.b) formIds.push(pair.b.id);
  }

  const url = `https://${
    walkthroughShopName.value
  }.myshopify.com?p1_walkthrough_pick=${formIds.join(',')}`;
  walkthroughPickerWindow = window.open(url, '_blank');
}

/** Handle postMessage from the storefront picker */
async function handlePickerMessage(event: MessageEvent) {
  const data = event.data;
  if (!data || data.type !== 'myuser-walkthrough-form-picks') return;
  if (!store.projectId) return;

  console.log('[walkthrough] Received picker selections:', data.picks);

  // data.picks = { popup: 'formId' | null, slide_in: 'formId' | null, banner: 'formId' | null }
  const picks: Record<string, string | null> = data.picks || {};
  const deletePromises: Promise<unknown>[] = [];
  const updatePromises: Promise<unknown>[] = [];

  // Persist only explicitly chosen forms.
  for (const pair of walkthroughFormPairs.value) {
    const chosenId = picks[pair.type];
    const formA = pair.a;
    const formB = pair.b;
    const formsInPair = [formA, formB].filter(
      (form): form is NonNullable<typeof form> => Boolean(form)
    );

    if (!formsInPair.length) continue;

    if (chosenId) {
      const chosenForm = formsInPair.find((form) => form?.id === chosenId);
      const unchosenForms = formsInPair.filter((form) => form?.id !== chosenId);

      if (chosenForm) {
        updatePromises.push(
          updateLeadForm(store.projectId, chosenForm.id, {
            tags: (chosenForm.tags || []).filter(
              (tag) =>
                tag === 'walkthrough-origin' ||
                (tag !== 'walkthrough' && !tag.startsWith('walkthrough-'))
            )
          }).catch((err) => {
            console.warn(
              '[walkthrough] Failed to clean chosen form tags:',
              err
            );
          })
        );
      }

      for (const unchosen of unchosenForms) {
        deletePromises.push(
          deleteLeadForm(store.projectId, unchosen.id).catch((err) => {
            console.warn('[walkthrough] Failed to delete unchosen form:', err);
          })
        );
      }
    } else {
      // Nothing picked for this type — delete both generated walkthrough forms.
      console.log(
        `[walkthrough] Type ${pair.type} skipped — deleting both walkthrough variants`
      );
      for (const form of formsInPair) {
        deletePromises.push(
          deleteLeadForm(store.projectId, form.id).catch((err) => {
            console.warn('[walkthrough] Failed to delete skipped form:', err);
          })
        );
      }
    }
  }

  await Promise.all(updatePromises);
  await Promise.all(deletePromises);

  // Mark forms as picked (tenant-level) so subsequent users skip the picker
  store.walkthroughFormsPicked = true;
  store.persistState();
  if (store.projectId) {
    updateProject(store.projectId, {
      walkthroughFormsPicked: true
    } as any).catch((err: unknown) =>
      console.warn('[walkthrough] Failed to sync formsPicked:', err)
    );
  }

  // Refresh the forms view immediately so the chosen forms appear on step 16.
  window.dispatchEvent(new CustomEvent('walkthrough-refresh-forms'));

  // Close the picker window if still open
  if (walkthroughPickerWindow && !walkthroughPickerWindow.closed) {
    walkthroughPickerWindow.close();
  }
  walkthroughPickerWindow = null;

  // Advance to next step
  store.next();
}

const cachedNewInsight = ref<string | null>(null);
const cachedMessages = ref<WalkthroughAIMessageChange[] | null>(null);
const walkthroughAILoading = ref(false);
// Timer ID for the message poll (cleared on step change to prevent stale dispatches)
let messagePollTimer: ReturnType<typeof setInterval> | null = null;
let walkthroughRefreshRequestCounter = 0;
// Pre-walkthrough session IDs — cached so revisits can restore "before" state
const cachedPreSessionIds = ref<string[] | null>(null);
function handleSessionIdsCaptured(e: Event) {
  const ids = (e as CustomEvent).detail?.sessionIds;
  if (ids && !cachedPreSessionIds.value) {
    // Only capture once — on the first run, before walkthrough creates new sessions
    cachedPreSessionIds.value = ids;
  }
}

function getNewestWalkthroughSession(
  summaries: WalkthroughAISummary[] | null | undefined = cachedSummaries.value
) {
  const preIds = new Set(cachedPreSessionIds.value || []);
  const candidates = (summaries || []).filter(
    (summary) => summary.sessionId && !preIds.has(summary.sessionId)
  );
  const walkthroughCandidates = candidates.filter((summary) =>
    summary.sessionId.startsWith('wt-session-')
  );
  const pool =
    walkthroughCandidates.length > 0 ? walkthroughCandidates : candidates;

  return (
    [...pool].sort((a, b) => {
      const aTime = new Date(a.endTime || a.startTime || 0).getTime();
      const bTime = new Date(b.endTime || b.startTime || 0).getTime();
      return bTime - aTime;
    })[0] || null
  );
}

async function requestWalkthroughRefresh(timeoutMs = 5000) {
  const requestId = `walkthrough-refresh-${++walkthroughRefreshRequestCounter}`;

  return new Promise<void>((resolve) => {
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const cleanup = () => {
      if (settled) return;
      settled = true;
      window.removeEventListener(
        'walkthrough-refresh-complete',
        handleComplete
      );
      if (timer) clearTimeout(timer);
    };

    const handleComplete = (e: Event) => {
      const completedRequestId = (e as CustomEvent).detail?.requestId;
      if (completedRequestId !== requestId) return;
      cleanup();
      resolve();
    };

    window.addEventListener('walkthrough-refresh-complete', handleComplete);
    timer = setTimeout(() => {
      cleanup();
      resolve();
    }, timeoutMs);

    window.dispatchEvent(
      new CustomEvent('walkthrough-refresh', {
        detail: { requestId }
      })
    );
  });
}
// ── Interactive target boost (z-index above overlay) ──
let interactiveEl: HTMLElement | null = null;
let spotlightObserver: ResizeObserver | null = null;
let spotlightTargetEl: Element | null = null;
let spotlightScrollParent: Element | null = null;
let spotlightRafId: number | null = null;
let fallbackMutationObserver: MutationObserver | null = null;
let spotlightRecoveryTimer: ReturnType<typeof setInterval> | null = null;
let scrollLockActive = false;

function preventScrollHandler(e: Event) {
  e.preventDefault();
}

function enableScrollLock() {
  if (scrollLockActive) return;
  scrollLockActive = true;
  const opts = { passive: false } as const;
  window.addEventListener('wheel', preventScrollHandler, opts);
  window.addEventListener('touchmove', preventScrollHandler, opts);
  const appContainer = document.querySelector(
    '.app-container'
  ) as HTMLElement | null;
  if (appContainer) {
    appContainer.addEventListener('wheel', preventScrollHandler, opts);
    appContainer.addEventListener('touchmove', preventScrollHandler, opts);
  }
}

function disableScrollLock() {
  if (!scrollLockActive) return;
  scrollLockActive = false;
  window.removeEventListener('wheel', preventScrollHandler);
  window.removeEventListener('touchmove', preventScrollHandler);
  const appContainer = document.querySelector(
    '.app-container'
  ) as HTMLElement | null;
  if (appContainer) {
    appContainer.removeEventListener('wheel', preventScrollHandler);
    appContainer.removeEventListener('touchmove', preventScrollHandler);
  }
}

function stopFallbackWatch() {
  if (fallbackMutationObserver) {
    fallbackMutationObserver.disconnect();
    fallbackMutationObserver = null;
  }
}

function stopSpotlightTracking() {
  if (spotlightObserver) {
    spotlightObserver.disconnect();
    spotlightObserver = null;
  }
  if (spotlightScrollParent) {
    spotlightScrollParent.removeEventListener('scroll', onSpotlightScroll);
    spotlightScrollParent.removeEventListener('scroll', onScrollClamp);
    // Remove the walkthrough padding we added
    (spotlightScrollParent as HTMLElement).style.removeProperty(
      'padding-bottom'
    );
    (spotlightScrollParent as HTMLElement).style.removeProperty('padding-top');
    spotlightScrollParent = null;
  }
  window.removeEventListener('scroll', onSpotlightScroll, true);
  window.removeEventListener('scroll', onScrollClampWindow, true);
  // Clean up mobile .app-container listeners
  const appContainer = document.querySelector(
    '.app-container'
  ) as HTMLElement | null;
  if (appContainer) {
    appContainer.removeEventListener('scroll', onSpotlightScroll);
    appContainer.removeEventListener('scroll', onScrollClampWindow);
  }
  if (spotlightRafId !== null) {
    cancelAnimationFrame(spotlightRafId);
    spotlightRafId = null;
  }
  spotlightTargetEl = null;
  if (spotlightRecoveryTimer !== null) {
    clearInterval(spotlightRecoveryTimer);
    spotlightRecoveryTimer = null;
  }
  stopFallbackWatch();
}

function refreshSpotlightRect() {
  if (!spotlightTargetEl) return;

  // If the element is still in the DOM, always use it — even if temporarily
  // zero-sized (e.g. mid-transition). This prevents snapping to the wrong element.
  if (spotlightTargetEl.isConnected) {
    const rect = spotlightTargetEl.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      spotlightRect.value = rect;
    }
    // Zero-sized but still connected: keep current rect, don't re-search
    return;
  }

  // Element is detached — try to re-find by step target selector
  const step = store.currentStep;
  if (!step?.target) return;

  let newEl: Element | null = null;

  // For session-summary, target the specific new session by data-session-id
  // to avoid snapping to an older session summary after DOM rebuild.
  // NEVER fall back to the generic .session-summary-item selector — that
  // would match the first (oldest) session in the DOM.
  if (step.id === 'session-summary') {
    const newSessionId = getNewestWalkthroughSession()?.sessionId;
    if (newSessionId) {
      newEl = document.querySelector(
        `.session-summary-item[data-session-id="${newSessionId}"]`
      );
    }
    if (!newEl) {
      newEl = document.querySelector('.session-skeleton-item');
    }
    // If neither found, DON'T fall through to generic selector
  } else {
    newEl = document.querySelector(step.target);
  }

  if (
    newEl &&
    newEl !== spotlightTargetEl &&
    (newEl as HTMLElement).offsetParent !== null
  ) {
    const newRect = newEl.getBoundingClientRect();
    if (newRect.width > 0 && newRect.height > 0) {
      console.log('[spotlight] Re-acquired target after DOM rebuild');
      startSpotlightTracking(newEl);
      spotlightRect.value = newRect;
    }
  }
}

/**
 * When any scroll fires, use a rAF loop to smoothly track the element
 * until scrolling settles (position stops changing between frames).
 */
let rafScrolling = false;
function onSpotlightScroll() {
  if (rafScrolling) return;
  rafScrolling = true;
  let prevTop = -1;
  let settledFrames = 0;
  const track = () => {
    if (!spotlightTargetEl) {
      rafScrolling = false;
      return;
    }
    refreshSpotlightRect();
    const curTop = spotlightTargetEl.getBoundingClientRect().top;
    if (Math.abs(curTop - prevTop) < 0.5) {
      settledFrames++;
    } else {
      settledFrames = 0;
    }
    prevTop = curTop;
    // Stop after position is stable for 5 consecutive frames (~80ms)
    if (settledFrames < 5) {
      spotlightRafId = requestAnimationFrame(track);
    } else {
      rafScrolling = false;
      spotlightRafId = null;
    }
  };
  spotlightRafId = requestAnimationFrame(track);
}

/**
 * Clamp scroll on the timeline container so the spotlight target
 * can't be scrolled out of the container's visible area.
 * Uses the scroll parent's own bounding rect, not the full viewport.
 * Keeps the ENTIRE element visible (with padding) if it fits.
 */
let clampingScroll = false;
const CLAMP_PAD = 0;
function onScrollClamp() {
  if (clampingScroll || !spotlightTargetEl || !spotlightScrollParent) return;
  const elRect = spotlightTargetEl.getBoundingClientRect();
  const containerRect = spotlightScrollParent.getBoundingClientRect();

  const visibleTop = containerRect.top + CLAMP_PAD;
  // On mobile, the bottom-pinned tooltip covers the lower portion of the
  // viewport. Shrink the visible area so the spotlight target can't scroll
  // behind it.
  let visibleBottom = containerRect.bottom - CLAMP_PAD;
  if (isMobileViewport()) {
    const tooltipReserve = Math.min(window.innerHeight * 0.45, 260);
    visibleBottom = Math.min(
      visibleBottom,
      window.innerHeight - tooltipReserve
    );
  }
  const visibleH = visibleBottom - visibleTop;

  // If element fits within the visible area, keep it entirely visible
  if (elRect.height <= visibleH) {
    if (elRect.top < visibleTop) {
      // Element's top is above visible area — scroll up
      clampingScroll = true;
      spotlightScrollParent.scrollTop -= visibleTop - elRect.top;
      clampingScroll = false;
    } else if (elRect.bottom > visibleBottom) {
      // Element's bottom is below visible area — scroll down
      clampingScroll = true;
      spotlightScrollParent.scrollTop += elRect.bottom - visibleBottom;
      clampingScroll = false;
    }
  } else {
    // Element is taller than visible area — keep at least top visible
    if (elRect.top > visibleTop + 40) {
      clampingScroll = true;
      spotlightScrollParent.scrollTop += elRect.top - (visibleTop + 40);
      clampingScroll = false;
    } else if (elRect.bottom < visibleTop + 80) {
      clampingScroll = true;
      spotlightScrollParent.scrollTop -= visibleTop + 80 - elRect.bottom;
      clampingScroll = false;
    }
  }
}

/**
 * Clamp window-level scroll so spotlight target stays in viewport.
 * On mobile, account for the bottom-pinned tooltip taking up space.
 */
function onScrollClampWindow() {
  if (clampingScroll || !spotlightTargetEl) return;
  // Only clamp window scroll when there's no scroll-parent container
  // (i.e. for page-level targets like dashboard items)
  if (spotlightScrollParent) return;
  const rect = spotlightTargetEl.getBoundingClientRect();
  const elH = rect.height;
  const vpH = window.innerHeight;
  // On mobile, the tooltip takes ~40% of the bottom — restrict visible area
  const bottomReserved = isMobileViewport() ? Math.min(vpH * 0.4, 240) : 60;
  // On mobile, account for progress bar below navbar
  const topReserved = isMobileViewport() ? 56 + 30 : 80;
  const minVisible = Math.min(60, elH);

  // On mobile, .app-container is the scroll container (overflow-y: auto),
  // not the window. Detect it and scrollBy on the right element.
  const scroller = isMobileViewport()
    ? (document.querySelector('.app-container') as HTMLElement) || window
    : window;
  const doScroll = (dy: number) => {
    if (scroller === window) window.scrollBy(0, dy);
    else (scroller as HTMLElement).scrollTop += dy;
  };

  if (rect.bottom < topReserved + minVisible) {
    const overshoot = topReserved + minVisible - rect.bottom;
    clampingScroll = true;
    doScroll(-overshoot);
    clampingScroll = false;
  } else if (rect.top > vpH - bottomReserved - minVisible) {
    const overshoot = rect.top - (vpH - bottomReserved - minVisible);
    clampingScroll = true;
    doScroll(overshoot);
    clampingScroll = false;
  }
}

function startSpotlightTracking(el: Element) {
  stopSpotlightTracking();
  spotlightTargetEl = el;

  // Watch for size changes (typewriter, content expansion, etc.)
  // Throttled to avoid jitter from rapid typewriter updates (8ms/char)
  let resizeRafId: number | null = null;
  spotlightObserver = new ResizeObserver(() => {
    if (resizeRafId) return; // already scheduled
    resizeRafId = requestAnimationFrame(() => {
      resizeRafId = null;
      refreshSpotlightRect();
      // When element grows (e.g. typewriter, expanding events), keep it in view
      if (spotlightScrollParent && spotlightTargetEl) {
        const elRect = spotlightTargetEl.getBoundingClientRect();
        const cRect = spotlightScrollParent.getBoundingClientRect();
        // If element bottom is now below the container, scroll to keep it visible
        if (elRect.bottom > cRect.bottom) {
          clampingScroll = true;
          spotlightScrollParent.scrollTop += elRect.bottom - cRect.bottom;
          clampingScroll = false;
          refreshSpotlightRect();
        }
      }
    });
  });
  spotlightObserver.observe(el);

  // Watch for scroll in parent containers (timeline scroll)
  const scrollParent = el.closest('.timeline-content') as HTMLElement | null;
  if (scrollParent) {
    spotlightScrollParent = scrollParent;
    // Add top + bottom padding so items anywhere can scroll to center
    const containerH = scrollParent.clientHeight;
    scrollParent.style.paddingTop = containerH / 3 + 'px';
    scrollParent.style.paddingBottom = containerH / 2 + 'px';
    scrollParent.addEventListener('scroll', onSpotlightScroll, {
      passive: true
    });
    // Clamp scroll so highlighted element can't leave container
    scrollParent.addEventListener('scroll', onScrollClamp);
  }

  // Also track window/document-level scroll (page-level scrollIntoView)
  window.addEventListener('scroll', onSpotlightScroll, {
    passive: true,
    capture: true
  });
  window.addEventListener('scroll', onScrollClampWindow, { capture: true });

  // On mobile, .app-container is the real scroll container (overflow-y: auto).
  // Attach scroll listeners there too so spotlight tracks during dashboard scroll.
  if (isMobileViewport()) {
    const appContainer = document.querySelector(
      '.app-container'
    ) as HTMLElement | null;
    if (appContainer) {
      appContainer.addEventListener('scroll', onSpotlightScroll, {
        passive: true
      });
      appContainer.addEventListener('scroll', onScrollClampWindow);
    }
  }

  // Periodic check: if the DOM element gets detached (e.g. Vue re-render from
  // loadTimelineData), the ResizeObserver stops firing. Poll to detect this
  // and trigger auto-recovery in refreshSpotlightRect.
  if (spotlightRecoveryTimer !== null) clearInterval(spotlightRecoveryTimer);
  spotlightRecoveryTimer = setInterval(() => {
    if (spotlightTargetEl && !spotlightTargetEl.isConnected) {
      console.log('[spotlight] Tracked element detached, attempting recovery');
      refreshSpotlightRect();
    }
  }, 300);
}

function cleanupInteractive() {
  if (interactiveEl) {
    interactiveEl.classList.remove('walkthrough-interactive-target');
    interactiveEl.style.removeProperty('position');
    interactiveEl.style.removeProperty('z-index');
    interactiveEl = null;
  }
}

// ── Simulate step button logic ──
const MIN_ACTIONS = 3;
const simDoneCount = computed(() => simRef.value?.doneCount ?? 0);
// Cached count: survives simRef.reset() so session-loading event gets the real number
const lastSimDoneCount = ref(0);
async function simBackToMenu() {
  if (!simRef.value) return;
  // If user typed a search query, submit it before going back to menu
  if (simRef.value.view === 'search' && simRef.value.searchInput?.trim()) {
    await simRef.value.submitSearch();
    return; // submitSearch already sets view back to 'menu'
  }
  // If user filled out a form, submit it before going back
  if (simRef.value.view === 'form' && simRef.value.hasAnyAnswer) {
    await simRef.value.submitForm();
    return; // submitForm already sets view back to 'menu'
  }
  simRef.value.view = 'menu';
}
const simIsInSubView = computed(
  () =>
    store.currentStep?.showcase === 'simulate-interactive' &&
    simRef.value?.view !== 'menu'
);
const simStepNeedsMore = computed(
  () =>
    store.currentStep?.showcase === 'simulate-interactive' &&
    simDoneCount.value < MIN_ACTIONS
);
const simActionLabel = computed(() => {
  if (store.currentStep?.showcase === 'simulate-interactive') {
    if (simDoneCount.value < MIN_ACTIONS) {
      return `Try ${MIN_ACTIONS - simDoneCount.value} more action${
        MIN_ACTIONS - simDoneCount.value === 1 ? '' : 's'
      }`;
    }
    return 'See what happened →';
  }
  return store.currentStep?.actionLabel || 'Next →';
});

// ── Range drag objective ──
const rangeDragged = ref(false);
function onRangeDragged() {
  rangeDragged.value = true;
}
onMounted(() => {
  window.addEventListener('range-dragged', onRangeDragged);
});
onUnmounted(() => {
  window.removeEventListener('range-dragged', onRangeDragged);
});
// Reset when step changes
watch(
  () => store.currentStepIndex,
  () => {
    rangeDragged.value = false;
  }
);

// ── Off-track detection ──
const isTransitioning = ref(false);
const isClickThroughAdvanceInProgress = ref(false);

/** True when the current step requires a userID but one hasn't been generated yet */
const awaitingUserGeneration = computed(() => {
  if (!store.isActive || !store.currentStep) return false;
  const resolvedRoute = store.currentStep.route;
  if (!resolvedRoute) return false;
  // The resolved route will have empty segments (e.g. /user//PROJECT) when targetUserId is null
  return resolvedRoute.includes('//');
});

const isOffTrack = computed(() => {
  if (isTransitioning.value) return false;
  if (awaitingUserGeneration.value) return false;
  if (!store.isActive || !store.currentStep?.route) return false;

  // During the customers click-through step, navigating to any user page is expected
  if (
    store.currentStep.id === 'customers-table' &&
    route.path.startsWith('/user/')
  ) {
    return false;
  }

  const stepRoute = store.currentStep.route;
  if (route.path === stepRoute) return false;
  // Compare route prefixes (e.g. /dashboard vs /user)
  const stepPrefix = stepRoute.split('/').slice(0, 2).join('/');
  const currentPrefix = route.path.split('/').slice(0, 2).join('/');
  return stepPrefix !== currentPrefix || stepPrefix.length <= 1;
});

const offTrackPageName = computed(() => {
  const r = store.currentStep?.route || '';
  if (r.includes('/dashboard')) return 'Dashboard';
  if (r.includes('/user/')) return 'Customer Timeline';
  if (r.includes('/templates')) return 'Templates';
  if (r.includes('/forms')) return 'Forms';
  if (r.includes('/results')) return 'Results';
  if (r.includes('/simulate')) return 'Simulate';
  if (r.includes('/organization')) return 'Organization';
  if (r.includes('/integrations')) return 'Integrations';
  return 'the tour';
});

async function returnToTour() {
  await navigateIfNeeded();
  await nextTick();
  await new Promise((r) => setTimeout(r, 400));
  await updateSpotlight();
}

function startShowcaseAnimation() {
  stopShowcaseAnimation();
  stopLfCycle();
  showcaseStep.value = 0;
  lfActiveType.value = 'popup';
  showcaseTimer = setInterval(() => {
    showcaseStep.value++;
    if (showcaseStep.value === 3) {
      startLfCycle();
    }
    if (showcaseStep.value >= 6) {
      stopShowcaseAnimation();
    }
  }, 540);
}

function stopShowcaseAnimation() {
  if (showcaseTimer) {
    clearInterval(showcaseTimer);
    showcaseTimer = null;
  }
}

function stopLfCycle() {
  if (lfCycleTimer) {
    clearInterval(lfCycleTimer);
    lfCycleTimer = null;
  }
}

function startLfCycle() {
  if (lfCycleTimer) return;
  const types: Array<'popup' | 'slidein' | 'banner'> = [
    'popup',
    'slidein',
    'banner'
  ];
  let idx = 0;
  lfCycleTimer = setInterval(() => {
    idx = (idx + 1) % types.length;
    lfActiveType.value = types[idx];
  }, 2800);
}

function pickLfType(t: 'popup' | 'slidein' | 'banner') {
  lfActiveType.value = t;
  // restart cycle from this choice
  if (lfCycleTimer) {
    clearInterval(lfCycleTimer);
    lfCycleTimer = null;
  }
  startLfCycle();
}

// ── Confetti style generator ──
const CONFETTI_COLORS = [
  '#6366f1',
  '#a855f7',
  '#ec4899',
  '#fbbf24',
  '#34d399',
  '#60a5fa'
];
function confettiStyle(i: number) {
  const color = CONFETTI_COLORS[i % CONFETTI_COLORS.length];
  const left = Math.random() * 100;
  const delay = Math.random() * 2;
  const duration = 2 + Math.random() * 2;
  const size = 6 + Math.random() * 8;
  const rotation = Math.random() * 360;
  return {
    '--confetti-color': color,
    left: `${left}%`,
    width: `${size}px`,
    height: `${size * 0.4}px`,
    animationDelay: `${delay}s`,
    animationDuration: `${duration}s`,
    transform: `rotate(${rotation}deg)`
  };
}

// ── Compute tooltip style ──
const isMobileViewport = () => window.innerWidth <= 640;

// Override tooltip body text for certain steps based on context
const tooltipBodyOverride = computed(() => {
  if (
    store.currentStep?.id === 'user-timeline-intro' &&
    !demoStore.isDemoMode
  ) {
    return "AI wrote this email based on the customer's browsing and purchase history. Click to preview the full email.";
  }
  if (store.currentStep?.id === 'customers-table') {
    if (demoStore.isDemoMode) {
      return 'Click any customer to see their full timeline.';
    }
    if (store.sandboxCustomer?.name) {
      return `Click ${store.sandboxCustomer.name} to see their full timeline.`;
    }
  }
  return '';
});

const tooltipStyle = computed(() => {
  // ── MOBILE: always pin tooltip to bottom of screen ──
  if (isMobileViewport()) {
    // Center modals / showcases stay centered on mobile
    if (
      !spotlightRect.value ||
      store.currentStep?.placement === 'center' ||
      store.currentStep?.showcase
    ) {
      return {
        position: 'fixed' as const,
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)'
      };
    }
    // All other steps: pin to bottom
    return {
      position: 'fixed' as const,
      bottom: '8px',
      left: '8px',
      right: '8px',
      top: 'auto',
      transform: 'none',
      width: 'auto'
    };
  }

  // ── DESKTOP ──
  // Templates page: keep tooltip in the free bottom-right area
  if (store.currentStep?.id === 'templates-grid') {
    return {
      position: 'fixed' as const,
      bottom: '24px',
      right: '24px',
      top: 'auto',
      left: 'auto',
      transform: 'none'
    };
  }

  // Forms return step: dock below the navbar instead of anchoring above the
  // grid, which can push the card off-screen on shorter desktop viewports.
  if (store.currentStep?.id === 'forms-grid') {
    return {
      position: 'fixed' as const,
      bottom: '24px',
      left: 'calc(50% + 56px)',
      right: 'auto',
      top: 'auto',
      transform: 'translateX(-50%)',
      width: '400px',
      maxWidth: 'calc(100vw - 180px)'
    };
  }

  // Compact clickThrough: pin just above the spotlight target, centered
  if (store.currentStep?.compact && spotlightRect.value) {
    const rect = spotlightRect.value;
    const GAP = 20;
    return {
      position: 'fixed' as const,
      top: Math.max(4, rect.top - 48 - GAP) + 'px',
      left: rect.left + rect.width / 2 + 'px',
      transform: 'translateX(-50%)'
    };
  }

  if (!spotlightRect.value || store.currentStep?.placement === 'center') {
    // For clickThrough steps, pin to bottom-right so the content is fully visible
    if (store.currentStep?.clickThrough) {
      return {
        position: 'fixed' as const,
        bottom: '24px',
        right: '24px',
        top: 'auto',
        left: 'auto',
        transform: 'none'
      };
    }
    return {
      position: 'fixed' as const,
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)'
    };
  }

  const rect = spotlightRect.value;
  const placement = store.currentStep?.placement || 'bottom';
  const GAP = 16;
  const TOOLTIP_WIDTH = 440;
  const TOOLTIP_HEIGHT = store.currentStep?.compact ? 72 : 320;
  const VIEWPORT_MARGIN = 12;

  let top = 0;
  let left = 0;
  let finalPlacement = placement;

  switch (placement) {
    case 'bottom':
      top = rect.bottom + GAP;
      left = rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2;
      // If tooltip would overflow the bottom, flip to top
      if (top + TOOLTIP_HEIGHT > window.innerHeight - VIEWPORT_MARGIN) {
        const topCandidate = rect.top - GAP - TOOLTIP_HEIGHT;
        if (topCandidate >= VIEWPORT_MARGIN) {
          top = rect.top - GAP;
          finalPlacement = 'top';
        }
      }
      break;
    case 'top':
      top = rect.top - GAP;
      left = rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2;
      // If tooltip would overflow the top, flip to bottom
      if (top - TOOLTIP_HEIGHT < VIEWPORT_MARGIN) {
        const bottomCandidate = rect.bottom + GAP;
        if (
          bottomCandidate + TOOLTIP_HEIGHT <=
          window.innerHeight - VIEWPORT_MARGIN
        ) {
          top = bottomCandidate;
          finalPlacement = 'bottom';
        }
      }
      break;
    case 'left':
      top = rect.top + rect.height / 2;
      left = rect.left - GAP - TOOLTIP_WIDTH;
      break;
    case 'right':
      top = rect.top + rect.height / 2;
      left = rect.right + GAP;
      break;
  }

  // Clamp to viewport
  left = Math.max(
    VIEWPORT_MARGIN,
    Math.min(left, window.innerWidth - TOOLTIP_WIDTH - VIEWPORT_MARGIN)
  );

  if (finalPlacement === 'top') {
    top = Math.max(
      TOOLTIP_HEIGHT + VIEWPORT_MARGIN,
      Math.min(top, window.innerHeight - VIEWPORT_MARGIN)
    );
  } else if (finalPlacement === 'left' || finalPlacement === 'right') {
    top = Math.max(
      TOOLTIP_HEIGHT / 2 + VIEWPORT_MARGIN,
      Math.min(top, window.innerHeight - TOOLTIP_HEIGHT / 2 - VIEWPORT_MARGIN)
    );
    // Pin vertical position only for the insight step where the target resizes
    // (typewriter effect changes height). Other left/right steps should track normally.
    if (store.currentStep?.id === 'insight-updated') {
      if (pinnedTooltipTop.value !== null) {
        top = pinnedTooltipTop.value;
      } else {
        const pinVal = top;
        nextTick(() => {
          pinnedTooltipTop.value = pinVal;
        });
      }
    }
  } else {
    // bottom placement — clamp so the card doesn't overflow viewport bottom
    top = Math.max(
      VIEWPORT_MARGIN,
      Math.min(top, window.innerHeight - TOOLTIP_HEIGHT - VIEWPORT_MARGIN)
    );
  }

  // Shrink tooltip width to fit when placed right and it would overflow
  let width: string | undefined;
  if (finalPlacement === 'right') {
    const availableWidth = window.innerWidth - (rect.right + GAP) - 12;
    if (availableWidth < TOOLTIP_WIDTH) {
      width = Math.max(260, availableWidth) + 'px';
      left = rect.right + GAP;
    }
  }

  const transform =
    finalPlacement === 'top'
      ? 'translateY(-100%)'
      : finalPlacement === 'left' || finalPlacement === 'right'
      ? 'translateY(-50%)'
      : '';

  return {
    position: 'fixed' as const,
    top: top + 'px',
    left: left + 'px',
    transform,
    ...(width ? { width, minWidth: '260px' } : {})
  };
});

const pulseStyle = computed(() => {
  if (!spotlightRect.value) return {};
  const rect = spotlightRect.value;
  return {
    position: 'fixed' as const,
    top: rect.top - 4 + 'px',
    left: rect.left - 4 + 'px',
    width: rect.width + 8 + 'px',
    height: rect.height + 8 + 'px'
  };
});

// ── Listen for clicks on the real page during clickThrough steps ──
function handleDocumentClick(e: MouseEvent) {
  if (!store.isActive || !store.currentStep?.clickThrough) return;

  // Don't capture clicks on the tooltip or progress bar
  const target = e.target as HTMLElement;
  if (
    target.closest('.walkthrough-tooltip') ||
    target.closest('.walkthrough-progress-bar')
  )
    return;

  // Mark as transitioning so off-track detection ignores route change
  isTransitioning.value = true;

  // Reset transition guard if user clicks but does not navigate
  setTimeout(() => {
    if (store.currentStep?.clickThrough && !route.path.startsWith('/user/')) {
      isTransitioning.value = false;
    }
  }, 3000);
}

// ── Find and highlight target element (with retries) ──
async function updateSpotlight() {
  const step = store.currentStep;
  if (!step?.target) {
    stopSpotlightTracking();
    spotlightRect.value = null;
    return;
  }

  // Only wait for delay on page-transition steps (no beforeShow = data isn't pre-loaded)
  if (step.delay && !step.beforeShow) {
    await new Promise((resolve) => setTimeout(resolve, step.delay));
  }

  await nextTick();

  // Try to find the element immediately, retry only if not found
  let el: Element | null = null;
  let usingFallback = false;
  for (let attempt = 0; attempt < 10; attempt++) {
    if (step.id === 'session-summary') {
      // Find the newest walkthrough session by data-session-id.
      const newSessionId = getNewestWalkthroughSession()?.sessionId;
      if (newSessionId) {
        el = document.querySelector(
          `.session-summary-item[data-session-id="${newSessionId}"]`
        );
      }
      // Prefer skeleton over old sessions (skeleton is the correct target
      // during AI loading before the real new session exists in the DOM)
      if (!el) {
        el = document.querySelector('.session-skeleton-item');
      }
    } else if (step.id === 'user-timeline-intro' && !demoStore.isDemoMode) {
      // Real tenant: prefer sent AI email over scheduled
      el =
        document.querySelector(
          '.timeline-item.email-item.imessage-email-sent'
        ) || document.querySelector(step.target);
    } else if (step.id === 'customers-table' && demoStore.isDemoMode) {
      // Demo tenants don't have a sandbox row — highlight the whole table so all rows look clickable
      el = document.querySelector('.customers-table-wrapper');
    } else if (step.id === 'scheduled-messages') {
      // Target the LAST scheduled wrapper — the AI rewrite animation
      // typically happens on a lower/newer scheduled email
      const all = document.querySelectorAll('.scheduled-wrapper');
      el = all.length > 0 ? all[all.length - 1] : null;
    } else {
      el = document.querySelector(step.target);
    }
    if (el && (el as HTMLElement).offsetParent !== null) {
      const testRect = el.getBoundingClientRect();
      if (testRect.width > 0 && testRect.height > 0) break;
    }
    el = null;
    // Only wait on retries, not the first attempt
    if (attempt === 0) {
      await nextTick();
    } else {
      await new Promise((resolve) => setTimeout(resolve, 80));
    }
  }

  // If primary target not found, try fallback (e.g. skeleton loader)
  if (!el && step.fallbackTarget) {
    const fb = document.querySelector(step.fallbackTarget);
    if (fb && (fb as HTMLElement).offsetParent !== null) {
      const fbRect = fb.getBoundingClientRect();
      if (fbRect.width > 0 && fbRect.height > 0) {
        el = fb;
        usingFallback = true;
      }
    }
  }

  if (el) {
    cleanupInteractive();

    // Scroll element into center of its scroll container
    const scrollParent = el.closest('.timeline-content') as HTMLElement | null;
    if (scrollParent) {
      // Add top padding so items near the start can scroll to center
      const pH = scrollParent.clientHeight;
      if (
        !scrollParent.style.paddingTop ||
        parseInt(scrollParent.style.paddingTop) < pH / 3
      ) {
        scrollParent.style.paddingTop = pH / 3 + 'px';
      }
      // Manually calculate scroll position to center element in container
      const containerRect = scrollParent.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      const elCenterInContainer =
        elRect.top -
        containerRect.top +
        scrollParent.scrollTop +
        elRect.height / 2;
      const targetScrollTop = elCenterInContainer - containerRect.height / 2;
      scrollParent.scrollTo({
        top: Math.max(0, targetScrollTop),
        behavior: 'smooth'
      });
      // Wait for smooth scroll to settle
      await new Promise<void>((resolve) => {
        let prev = scrollParent.scrollTop;
        let settled = 0;
        const check = () => {
          const cur = scrollParent.scrollTop;
          if (Math.abs(cur - prev) < 1) settled++;
          else settled = 0;
          prev = cur;
          if (settled < 5) requestAnimationFrame(check);
          else resolve();
        };
        requestAnimationFrame(check);
      });
    }

    const rect = el.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) {
      spotlightRect.value = null;
      return;
    }
    spotlightRect.value = rect;

    // Page-level scroll if needed (only for non-timeline targets)
    const topThreshold = isMobileViewport() ? 56 + 30 : 80; // navbar + progress bar on mobile
    const bottomThreshold = isMobileViewport()
      ? window.innerHeight - Math.min(window.innerHeight * 0.4, 240)
      : window.innerHeight - 80;
    if (
      !scrollParent &&
      (rect.top < topThreshold || rect.bottom > bottomThreshold)
    ) {
      // On mobile, scrollIntoView scrolls .app-container (overflow-y: auto)
      // Use 'start' on mobile so the element appears above the bottom tooltip
      // Add scroll-margin so the element doesn't sit flush at the top
      if (isMobileViewport()) {
        (el as HTMLElement).style.scrollMarginTop = '120px';
      }
      el.scrollIntoView({
        behavior: 'smooth',
        block: isMobileViewport() ? 'start' : 'center'
      });
      // Wait for scroll to settle before reading the rect again
      await new Promise((resolve) => setTimeout(resolve, 400));
      (el as HTMLElement).style.scrollMarginTop = '';
      const updatedRect = el.getBoundingClientRect();
      if (updatedRect.width > 0 && updatedRect.height > 0) {
        spotlightRect.value = updatedRect;
      }
    }

    startSpotlightTracking(el);

    // If using fallback, watch for the real target to appear and switch
    if (usingFallback && step.target) {
      stopFallbackWatch();
      const targetSel = step.target;
      fallbackMutationObserver = new MutationObserver(() => {
        const real = document.querySelector(targetSel);
        if (real && (real as HTMLElement).offsetParent !== null) {
          const r = real.getBoundingClientRect();
          if (r.width > 0 && r.height > 0) {
            stopFallbackWatch();
            // Switch spotlight to real element
            spotlightRect.value = r;
            startSpotlightTracking(real);
          }
        }
      });
      fallbackMutationObserver.observe(document.body, {
        childList: true,
        subtree: true
      });
    }

    if (step.interactive) {
      const htmlEl = el as HTMLElement;
      htmlEl.classList.add('walkthrough-interactive-target');
      htmlEl.style.position = 'relative';
      htmlEl.style.zIndex = '10001';
      interactiveEl = htmlEl;
    }
  } else {
    stopSpotlightTracking();
    spotlightRect.value = null;
  }
}

// ── Route navigation for steps ──
async function navigateIfNeeded() {
  const step = store.currentStep;
  if (!step?.route) return;

  // If the step route requires a userID but one hasn't been generated yet, wait
  if (
    step.route.includes(':userID') ||
    (step.route.includes('//') && !store.targetUserId)
  ) {
    if (!store.targetUserId) return; // handled by the generating overlay
  }

  const targetPath = step.route;

  // If already on a matching page pattern, don't re-navigate
  // e.g., already on /user/SOME_ID/PROJECT matches /user/:userID/:projectID
  if (route.path === targetPath) return;

  // Check if we're already on the right type of page (e.g., any user page)
  const targetPrefix = targetPath.split('/').slice(0, 2).join('/');
  const currentPrefix = route.path.split('/').slice(0, 2).join('/');
  if (targetPrefix === currentPrefix && targetPrefix.length > 1) {
    // Already on the right kind of page — no need to wait, updateSpotlight handles it
    return;
  }

  await router.push(targetPath);
  // Wait for route transition + rendering
  await new Promise((resolve) => setTimeout(resolve, step.delay || 500));
}

// ── Execute beforeShow actions ──
async function executeBeforeShow() {
  const step = store.currentStep;
  if (!step?.beforeShow) return;

  if (step.beforeShow === 'open-simulate') {
    // Programmatically open the simulate panel
    await new Promise((resolve) => setTimeout(resolve, 200));
    const simBtn = document.querySelector(
      '.desktop-only .sim-trigger-btn'
    ) as HTMLElement;
    if (simBtn) {
      // Only click if panel isn't already open
      const popup = document.querySelector('.sim-popup');
      if (!popup) simBtn.click();
    }
    // Wait for panel to open and render
    await new Promise((resolve) => setTimeout(resolve, 400));
  }

  if (step.beforeShow === 'refresh-timeline') {
    // On mobile, ensure we're on the Timeline tab
    if (isMobileViewport()) {
      window.dispatchEvent(
        new CustomEvent('walkthrough-switch-tab', {
          detail: { tab: 'timeline' }
        })
      );
      await new Promise((resolve) => setTimeout(resolve, 200));
    }

    // Check if we already have cached AI data (e.g. going back to this step)
    if (store.aiComplete && cachedSummaries.value) {
      // Restore walkthrough state on the (possibly remounted) timeline component
      window.dispatchEvent(
        new CustomEvent('walkthrough-restore-session-state', {
          detail: { preSessionIds: cachedPreSessionIds.value || [] }
        })
      );

      // Ensure the new session is expanded (it may have been collapsed
      // if the timeline re-rendered while on a different step)
      const newSession = getNewestWalkthroughSession(cachedSummaries.value);

      // Expand the new session in the timeline data
      window.dispatchEvent(
        new CustomEvent('walkthrough-expand-session', {
          detail: { sessionId: newSession?.sessionId }
        })
      );

      // Wait for DOM to settle after expansion
      await nextTick();
      await new Promise((resolve) => setTimeout(resolve, 200));

      // Replay session typewriter animation — pass cached summary so it doesn't
      // have to search for it (component may have remounted)
      if (newSession?.summary) {
        window.dispatchEvent(
          new CustomEvent('walkthrough-session-typewriter', {
            detail: {
              summary: newSession.summary,
              sessionId: newSession.sessionId
            }
          })
        );
      } else {
        window.dispatchEvent(new CustomEvent('walkthrough-session-typewriter'));
      }

      // Wait for typewriter to start and DOM to render
      await nextTick();
      await new Promise((resolve) => setTimeout(resolve, 150));

      // Now spotlight and show tooltip
      await updateSpotlight();
      tooltipReady.value = true;
      return;
    }

    // Page reload: AI already completed but cached data is gone.
    // Session summaries are already in Firestore and will be loaded by
    // the normal loadTimelineData() call — no need to re-stream or show skeleton.
    if (store.aiComplete && !cachedSummaries.value) {
      // Wait for the timeline to finish loading customer data
      await new Promise((resolve) => setTimeout(resolve, 600));
      await updateSpotlight();
      tooltipReady.value = true;
      return;
    }

    // Show loading skeleton in the timeline
    window.dispatchEvent(new CustomEvent('walkthrough-snapshot-messages'));
    window.dispatchEvent(
      new CustomEvent('walkthrough-session-loading', {
        detail: { actionCount: lastSimDoneCount.value }
      })
    );

    // Wait for skeleton to render, then spotlight it
    await nextTick();
    await new Promise((resolve) => setTimeout(resolve, 300));
    await updateSpotlight();
    // Skeleton is now spotlighted — show the tooltip
    tooltipReady.value = true;

    // Refetch customer early so simulate actions appear in the skeleton's events list
    // (runs in parallel with the AI stream below).
    const earlyRefreshPromise = requestWalkthroughRefresh();

    // Fire the combined walkthrough AI SSE endpoint
    const projectId = store.projectId || demoStore.demoTenantId;
    const customerId = store.targetUserId;
    if (!projectId || !customerId) {
      console.error('[walkthrough-ai] Missing projectId or customerId');
      window.dispatchEvent(new CustomEvent('walkthrough-session-ready'));
      return;
    }

    walkthroughAILoading.value = true;
    store.setAiComplete(false);

    try {
      await streamWalkthroughAI(projectId, customerId, {
        onSummary: (summaries) => {
          console.log(
            '[walkthrough-ai] Session summaries arrived:',
            summaries.length
          );
          cachedSummaries.value = summaries;
          // Find the new session's summary and start the typewriter immediately
          // on the skeleton (before AI stream fully completes)
          const newSession = getNewestWalkthroughSession(summaries);
          if (newSession?.summary) {
            window.dispatchEvent(
              new CustomEvent('walkthrough-session-typewriter', {
                detail: {
                  summary: newSession.summary,
                  sessionId: newSession.sessionId
                }
              })
            );
          }
        },
        onInsight: (previousInsight, newInsight) => {
          console.log(
            '[walkthrough-ai] Insight arrived, prevLen:',
            previousInsight?.length,
            'newLen:',
            newInsight?.length
          );
          cachedPreviousInsight.value = previousInsight;
          cachedNewInsight.value = newInsight;
        },
        onMessage: (messages) => {
          console.log('[walkthrough-ai] Messages arrived:', messages.length);
          cachedMessages.value = messages;
        },
        onError: (task, error) => {
          console.error(`[walkthrough-ai] Error in ${task}:`, error);
        },
        onDone: () => {
          console.log('[walkthrough-ai] All tasks complete');
        }
      });
    } catch (err) {
      console.error('[walkthrough-ai] Stream failed:', err);
    }

    walkthroughAILoading.value = false;
    store.setAiComplete(true);

    // Wait for the early refresh request to actually complete.
    await earlyRefreshPromise;

    // Refresh again to pick up AI-generated messages/summaries that were created
    // during the stream, and do not hide the skeleton until that request has
    // actually completed.
    await requestWalkthroughRefresh();

    // Hide skeleton (and build real session items in the same tick)
    window.dispatchEvent(new CustomEvent('walkthrough-session-ready'));
    // Wait for Vue to render the real session element (skeleton removal is instant)
    await nextTick();
    await nextTick();

    // Re-spotlight the real new session element (skeleton is now gone)
    await updateSpotlight();

    // Expand the new session explicitly (in case it rendered collapsed)
    window.dispatchEvent(
      new CustomEvent('walkthrough-expand-session', {
        detail: {
          sessionId: getNewestWalkthroughSession()?.sessionId
        }
      })
    );
    await nextTick();
    await new Promise((resolve) => setTimeout(resolve, 100));
    // Refresh spotlight rect once more after expand
    refreshSpotlightRect();

    // Typewriter was already started during the stream (in onSummary callback)
    // No need to re-dispatch here — the animation continues seamlessly on the real session
  }

  if (step.beforeShow === 'walkthrough-messages') {
    // On mobile, ensure we're on the Timeline tab (coming back from insight/profile)
    if (isMobileViewport()) {
      window.dispatchEvent(
        new CustomEvent('walkthrough-switch-tab', {
          detail: { tab: 'timeline' }
        })
      );
      await new Promise((resolve) => setTimeout(resolve, 300));
    }

    // Scroll to and spotlight the email FIRST, before any animation starts.
    // This prevents glitchy scroll during the typewriter.
    await updateSpotlight();
    tooltipReady.value = true;
    // Small pause so the spotlight settles before animation begins
    await new Promise((resolve) => setTimeout(resolve, 250));

    // Now start the typewriter animation
    if (cachedMessages.value && cachedMessages.value.length > 0) {
      console.log(
        '[walkthrough] Animating messages from cached AI data:',
        cachedMessages.value.length
      );
      window.dispatchEvent(
        new CustomEvent('walkthrough-animate-messages', {
          detail: { messages: cachedMessages.value }
        })
      );
    } else {
      console.log(
        '[walkthrough] Messages not ready yet — starting pending typewriter'
      );
      // Start the delete animation immediately using snapshotted content.
      // After deletion finishes the UI shows an "AI thinking" state.
      window.dispatchEvent(new CustomEvent('walkthrough-pending-typewriter'));

      // Poll until cachedMessages arrives — fire-and-forget (non-blocking)
      // so we don't hold up the tooltip or spotlight.
      // Store in component-level var so it can be cleared on step change.
      if (messagePollTimer) clearInterval(messagePollTimer);
      messagePollTimer = setInterval(() => {
        if (cachedMessages.value && cachedMessages.value.length > 0) {
          if (messagePollTimer) clearInterval(messagePollTimer);
          messagePollTimer = null;
          console.log(
            '[walkthrough] Messages arrived during pending typewriter:',
            cachedMessages.value.length
          );
          window.dispatchEvent(
            new CustomEvent('walkthrough-animate-messages', {
              detail: { messages: cachedMessages.value }
            })
          );
        }
      }, 200);
      // Safety: stop polling after 30s
      setTimeout(() => {
        if (messagePollTimer) {
          clearInterval(messagePollTimer);
          messagePollTimer = null;
        }
      }, 30000);
    }
    // Skip the normal updateSpotlight + tooltipReady below — already done above
    return;
  }

  if (step.beforeShow === 'walkthrough-insight') {
    // On mobile, the insight section lives in the Profile tab (UserInfoComponent)
    // which is hidden when mobileActiveTab === 'timeline'. Switch tabs first.
    if (isMobileViewport()) {
      window.dispatchEvent(
        new CustomEvent('walkthrough-switch-tab', {
          detail: { tab: 'profile' }
        })
      );
      // Wait for Vue to conditionally render UserInfoComponent
      await new Promise((resolve) => setTimeout(resolve, 400));
    }

    // Use cached insight data from the combined AI call
    if (
      cachedPreviousInsight.value !== null &&
      cachedNewInsight.value !== null
    ) {
      console.log('[walkthrough] Animating insight from cached AI data');
      window.dispatchEvent(
        new CustomEvent('walkthrough-animate-insight', {
          detail: {
            previousInsight: cachedPreviousInsight.value,
            newInsight: cachedNewInsight.value
          }
        })
      );
    } else {
      console.warn('[walkthrough] No cached insight data available');
      window.dispatchEvent(new CustomEvent('walkthrough-refresh'));
      await new Promise((resolve) => setTimeout(resolve, 200));
    }
  }

  if (step.beforeShow === 'generate-walkthrough-forms') {
    // If forms have already been picked by another team member, skip the picker
    // and go straight to the forms grid. The second user doesn't need to re-pick.
    if (store.walkthroughFormsPicked) {
      console.log(
        '[walkthrough] Forms already picked by another user — skipping picker'
      );
      store.next();
      return;
    }

    // Await the prefetch that was kicked off earlier (templates-grid step).
    // If it hasn't started yet for some reason, start it now.
    await prefetchWalkthroughForms();

    // Re-check after prefetch — the backend may have reported forms as already picked
    if (store.walkthroughFormsPicked) {
      console.log('[walkthrough] Forms already picked — skipping picker');
      store.next();
      return;
    }
  }

  if (step.beforeShow === 'scroll-to-email') {
    // Refresh customer data so the async-generated email HTML is picked up
    // (email content is generated in the background during sandbox creation)
    window.dispatchEvent(new CustomEvent('walkthrough-refresh'));
    await new Promise((resolve) => setTimeout(resolve, 600));

    // Ensure the Email filter is active so email items are visible
    const emailFilterBtn = document.querySelector(
      '.filter-btn:not(.active)'
    ) as HTMLElement;
    // Find the Email filter specifically
    const allFilterBtns = document.querySelectorAll('.filter-btn');
    for (const btn of allFilterBtns) {
      if (
        btn.textContent?.includes('Email') &&
        !btn.classList.contains('active')
      ) {
        (btn as HTMLElement).click();
        await new Promise((resolve) => setTimeout(resolve, 300));
        break;
      }
    }

    // Now scroll the timeline container to show the right email
    // For real tenants: prefer sent emails (the AI-generated past email)
    // For demo: prefer scheduled emails
    await new Promise((resolve) => setTimeout(resolve, 200));
    let emailEl: HTMLElement | null = null;
    // Prefer emails with generated content (has thumbnail), then scheduled, then any
    // Target .item-content inside so spotlight wraps just the visual card
    emailEl = document.querySelector(
      '.timeline-item.email-item.email-has-content .item-content'
    ) as HTMLElement;
    if (!emailEl) {
      if (!demoStore.isDemoMode) {
        emailEl = (document.querySelector(
          '.timeline-item.email-item.imessage-email-sent .item-content'
        ) ||
          document.querySelector(
            '.timeline-item.email-item .item-content'
          )) as HTMLElement;
      } else {
        emailEl = (document.querySelector(
          '.timeline-item.email-item.scheduled-item .item-content'
        ) ||
          document.querySelector(
            '.timeline-item.email-item .item-content'
          )) as HTMLElement;
      }
    }
    const scrollContainer = document.querySelector(
      '.timeline-content'
    ) as HTMLElement;
    if (emailEl && scrollContainer) {
      // Add top + bottom padding so items anywhere can scroll to center
      const containerH = scrollContainer.clientHeight;
      scrollContainer.style.paddingTop = containerH / 3 + 'px';
      scrollContainer.style.paddingBottom = containerH / 2 + 'px';
      // Manually center the element within the timeline container
      const containerRect = scrollContainer.getBoundingClientRect();
      const elRect = emailEl.getBoundingClientRect();
      const elCenterInContainer =
        elRect.top -
        containerRect.top +
        scrollContainer.scrollTop +
        elRect.height / 2;
      const targetScrollTop = elCenterInContainer - containerRect.height / 2;
      scrollContainer.scrollTo({
        top: Math.max(0, targetScrollTop),
        behavior: 'smooth'
      });
      // Wait for smooth scroll to settle
      await new Promise<void>((resolve) => {
        let prev = scrollContainer.scrollTop;
        let settled = 0;
        const check = () => {
          const cur = scrollContainer.scrollTop;
          if (Math.abs(cur - prev) < 1) settled++;
          else settled = 0;
          prev = cur;
          if (settled < 5) requestAnimationFrame(check);
          else resolve();
        };
        requestAnimationFrame(check);
      });
    }
  }
}

// ── Close simulate panel if it's open ──
function closeSimulatePanel() {
  const popup = document.querySelector('.sim-popup');
  if (popup) {
    const simBtn = document.querySelector(
      '.desktop-only .sim-trigger-btn'
    ) as HTMLElement;
    if (simBtn) simBtn.click(); // toggle it closed
  }
}

// ── Handle next step (navigate + spotlight) ──
async function handleNext() {
  // If this is the last step, complete & celebrate
  if (store.currentStepIndex >= store.totalSteps - 1) {
    // Clear spotlight before showing celebration so the last target
    // doesn't remain highlighted behind the CTA overlay
    stopSpotlightTracking();
    cleanupInteractive();
    spotlightRect.value = null;
    store.complete();
    showCelebration.value = true;
    return;
  }

  // Forms picker step: open the storefront picker instead of advancing
  if (store.currentStep?.id === 'forms-pick') {
    openWalkthroughPicker();
    return;
  }

  // Reset filters back to all-on when leaving the filter step
  if (store.currentStep?.id === 'user-timeline-filters') {
    cleanupInteractive();
    document.querySelectorAll('.filter-toggles .filter-btn').forEach((btn) => {
      if (!btn.classList.contains('active')) {
        (btn as HTMLElement).click();
      }
    });
    await new Promise((resolve) => setTimeout(resolve, 100));
  }

  // Clean up interactive element boost for any step
  cleanupInteractive();

  // Close simulate panel before leaving the step
  if (store.currentStep?.beforeShow === 'open-simulate') {
    closeSimulatePanel();
    await new Promise((resolve) => setTimeout(resolve, 200));
  }

  // Clear old spotlight immediately so it doesn't linger during page transition
  spotlightRect.value = null;

  // store.next() mutates currentStepIndex, which triggers the watcher
  // that calls navigateIfNeeded → executeBeforeShow → updateSpotlight.
  // Do NOT call those here or they will run twice.
  store.next();
}

function handleOverlayClick() {
  // Don't advance on overlay click for showcase, clickThrough, or interactive steps
  if (
    store.currentStep?.showcase ||
    store.currentStep?.clickThrough ||
    store.currentStep?.interactive
  )
    return;
  handleNext();
}

function closeCelebration() {
  showCelebration.value = false;
  stopCelebrationAnimation();
  window.dispatchEvent(new CustomEvent('walkthrough-clear-message-changes'));
  // Clear cached walkthrough AI data
  cachedSummaries.value = null;
  cachedPreviousInsight.value = null;
  cachedNewInsight.value = null;
  cachedMessages.value = null;
  store.setAiComplete(false);
  // Reset celebration project state
  projectIsActive.value = null;
  activatingProject.value = false;
  activationSuccess.value = false;
  celebrationLoading.value = false;
  // Navigate to dashboard
  if (store.projectId) {
    router.push('/dashboard/' + store.projectId);
  }
}

// ── Fetch project active status when celebration shows (non-demo) ──
async function fetchProjectActiveStatus() {
  if (demoStore.isDemoMode) return;
  const projectId = store.projectId;
  if (!projectId) return;

  celebrationLoading.value = true;
  try {
    const projectData = await getProject(projectId, {
      includeCustomers: false
    });
    projectIsActive.value =
      (projectData as Record<string, unknown>).isActive === true;
  } catch (err) {
    console.error('[walkthrough] Failed to fetch project status:', err);
    projectIsActive.value = false;
  } finally {
    celebrationLoading.value = false;
  }
}

async function activateProject() {
  const projectId = store.projectId;
  if (!projectId || activatingProject.value) return;

  activatingProject.value = true;
  try {
    const result = await setActiveStatus(projectId, true);
    if (result.success) {
      projectIsActive.value = true;
      activationSuccess.value = true;
    }
  } catch (err) {
    console.error('[walkthrough] Failed to activate project:', err);
  } finally {
    activatingProject.value = false;
  }
}

function replayTour() {
  showCelebration.value = false;
  // Clear cached walkthrough AI data for fresh replay
  cachedSummaries.value = null;
  cachedPreviousInsight.value = null;
  cachedNewInsight.value = null;
  cachedMessages.value = null;
  store.setAiComplete(false);
  if (demoStore.demoTenantId) {
    store.restart(demoStore.demoTenantId, store.targetUserId || undefined);
  }
}

function goToShopifyInstall() {
  showCelebration.value = false;
  router.push('/shopify');
}

// ── Keyboard navigation ──
function handleKeydown(e: KeyboardEvent) {
  if (showCelebration.value) {
    if (e.key === 'Escape' || e.key === 'Enter') closeCelebration();
    return;
  }
  if (!store.isActive) return;

  // If a page modal is blocking the walkthrough, arrow-right / Enter / Escape closes it
  if (modalOpen.value) {
    if (
      e.key === 'ArrowRight' ||
      e.key === 'Enter' ||
      e.key === 'Escape' ||
      e.key === ' '
    ) {
      e.preventDefault();
      // Close any known modals
      demoStore.showOnboarding = false;
      // Click the modal's close / X button if one exists
      const closeBtn = document.querySelector(
        '.modal-overlay .close-btn, .modal-overlay .btn-secondary, .modal-overlay [aria-label="Close"]'
      ) as HTMLElement;
      if (closeBtn) closeBtn.click();
    }
    return;
  }

  switch (e.key) {
    case 'ArrowRight':
    case 'Enter':
    case ' ':
      e.preventDefault();
      // On the interactive simulate step, mirror the button behaviour:
      // sub-view with pending input → submit it; needs more actions → back to menu; done → advance
      if (store.currentStep?.showcase === 'simulate-interactive') {
        if (
          simRef.value?.view === 'search' &&
          simRef.value?.searchInput?.trim()
        ) {
          simRef.value.submitSearch();
          return;
        }
        if (simRef.value?.view === 'form' && simRef.value?.hasAnyAnswer) {
          simRef.value.submitForm();
          return;
        }
        if (simIsInSubView.value || simStepNeedsMore.value) {
          simBackToMenu();
          return;
        }
        // All actions done and on menu → fall through to handleNext
      }
      handleNext();
      break;
    case 'ArrowLeft':
      e.preventDefault();
      simIsInSubView.value ? simBackToMenu() : store.prev();
      break;
    case 'Escape':
      store.dismiss();
      break;
  }
}

// ── Fetch project status when celebration shows (non-demo) ──
watch(showCelebration, (visible) => {
  if (visible) {
    startCelebrationAnimation();
    if (!demoStore.isDemoMode) {
      fetchProjectActiveStatus();
    }
  } else {
    stopCelebrationAnimation();
  }
});

// ── Watch for step changes ──
watch(
  () => store.currentStepIndex,
  async () => {
    if (!store.isActive) return;
    // Immediately clear previous step's highlight and tracking
    stopSpotlightTracking();
    cleanupInteractive();
    disableScrollLock();
    spotlightRect.value = null;
    pinnedTooltipTop.value = null;
    stopShowcaseAnimation();
    stopLfCycle();
    // Kill any stale message poll from a previous step
    if (messagePollTimer) {
      clearInterval(messagePollTimer);
      messagePollTimer = null;
    }
    // Capture sim count BEFORE reset so session-loading event gets the real number
    lastSimDoneCount.value = simDoneCount.value;
    simRef.value?.reset();
    // Hide tooltip until data is loaded / spotlight is positioned.
    // Also hide during beforeShow so users can't click buttons before data is ready
    // (e.g. "Open picker →" before shopName is fetched).
    const step = store.currentStep;
    const stepIdx = store.currentStepIndex;
    const needsSpotlight = !!step?.target;
    if (needsSpotlight || step?.beforeShow) tooltipReady.value = false;
    await navigateIfNeeded();
    await executeBeforeShow();
    // Guard: if step changed during beforeShow (e.g. auto-skipped), bail
    if (store.currentStepIndex !== stepIdx) return;
    // Some beforeShow handlers (e.g. walkthrough-messages) return early after
    // handling their own spotlight + tooltip. If tooltipReady is already true,
    // skip the redundant updateSpotlight.
    if (!tooltipReady.value) {
      await updateSpotlight();
      tooltipReady.value = true;
    }
    // Lock scroll if this step requires it (e.g. session-summary)
    if (store.currentStep?.noScroll) {
      enableScrollLock();
    }
    // Prefetch walkthrough forms in the background once we reach the Results group
    // (several steps before forms-pick) so data is ready when the user gets there.
    if (
      store.currentStep?.group === 'Results' ||
      store.currentStep?.group === 'Templates'
    ) {
      prefetchWalkthroughForms();
    }
    // Start showcase animation if this step has one
    if (
      store.currentStep?.showcase === 'simulate' ||
      store.currentStep?.showcase === 'integrations' ||
      store.currentStep?.showcase === 'welcome' ||
      store.currentStep?.showcase === 'forms' ||
      store.currentStep?.showcase === 'forms-pick'
    ) {
      startShowcaseAnimation();
    }
  }
);

// ── Auto-dismiss if user navigates to a non-project route (logo, back button, etc.) ──
watch(
  () => route.path,
  (newPath) => {
    if (!store.isActive) return;
    // Non-project routes: landing, auth, projects list, shopify install, etc.
    const nonProjectPaths = [
      '/',
      '/auth',
      '/projects',
      '/shopify',
      '/terms',
      '/privacy'
    ];
    if (nonProjectPaths.includes(newPath)) {
      store.dismiss();
    }
  }
);

// ── Handle click-through route transitions (e.g. customer row click) ──
watch(
  () => route.path,
  async (newPath) => {
    if (!store.isActive) return;

    const step = store.currentStep;
    if (!step?.clickThrough || step.id !== 'customers-table') return;
    if (!newPath.startsWith('/user/')) return;
    if (isClickThroughAdvanceInProgress.value) return;

    isClickThroughAdvanceInProgress.value = true;
    isTransitioning.value = true;

    // Stop spotlight tracking immediately so the recovery timer doesn't
    // try to re-acquire .sandbox-row on a page where it no longer exists
    stopSpotlightTracking();
    spotlightRect.value = null;

    const match = newPath.match(/\/user\/([^/]+)\//);
    if (match && match[1]) {
      store.setTargetUser(match[1]);
    }

    // Wait for the user page to render before advancing
    await new Promise((resolve) => setTimeout(resolve, 800));
    await handleNext();

    isTransitioning.value = false;
    isClickThroughAdvanceInProgress.value = false;
  }
);

// ── Watch for window resize ──
let resizeTimeout: ReturnType<typeof setTimeout>;
function handleResize() {
  clearTimeout(resizeTimeout);
  resizeTimeout = setTimeout(() => updateSpotlight(), 150);
}

// ── Lifecycle ──
onMounted(() => {
  window.addEventListener('keydown', handleKeydown);
  window.addEventListener('resize', handleResize);
  window.addEventListener('message', handlePickerMessage);
  window.addEventListener(
    'walkthrough-session-ids-captured',
    handleSessionIdsCaptured
  );
  document.addEventListener('click', handleDocumentClick, true);

  // Watch for page modals (.modal-overlay) appearing — hide walkthrough while open
  modalObserver = new MutationObserver(() => {
    const hasModal = !!document.querySelector(
      '.modal-overlay, .modal-backdrop, .idea-editor-overlay, .time-editor-overlay, .sms-editor-overlay, .text-email-editor-overlay'
    );
    modalOpen.value = hasModal;
  });
  modalObserver.observe(document.body, { childList: true, subtree: true });

  if (store.isActive) {
    // Re-fetch sandbox customer data if restored from localStorage (page refresh)
    store.hydrate().then(() => {
      navigateIfNeeded().then(async () => {
        await executeBeforeShow();
        await updateSpotlight();
        if (
          store.currentStep?.showcase === 'simulate' ||
          store.currentStep?.showcase === 'integrations' ||
          store.currentStep?.showcase === 'welcome' ||
          store.currentStep?.showcase === 'forms' ||
          store.currentStep?.showcase === 'forms-pick'
        )
          startShowcaseAnimation();
      });
    });
  }
});

// ── Dev helper: replay from simulate step without re-creating tenant ──
// Usage: open browser console → __wtReplay()
// Optionally: __wtReplay(8) to go to a specific step index
(window as any).__wtReplay = (stepIndex?: number) => {
  // Default to simulate-showcase (step 8)
  const target =
    stepIndex ?? store.steps.findIndex((s) => s.id === 'simulate-showcase');
  // Clear cached AI data so refresh-timeline re-streams
  store.setAiComplete(false);
  cachedSummaries.value = null;
  cachedPreviousInsight.value = null;
  cachedNewInsight.value = null;
  cachedMessages.value = null;
  cachedPreSessionIds.value = null;
  walkthroughAILoading.value = false;
  formsPrefetchPromise = null; // allow re-prefetch on replay
  // Clear timeline walkthrough state
  window.dispatchEvent(new CustomEvent('walkthrough-clear-message-changes'));
  // Stop any running spotlight / animation
  stopSpotlightTracking();
  cleanupInteractive();
  stopShowcaseAnimation();
  // Jump to step
  store.goToStep(target >= 0 ? target : 8);
  console.log(
    `[__wtReplay] Reset to step ${store.currentStepIndex} (${store.currentStep?.id})`
  );
};

// ── Dev helper: show the celebration / go-live screen ──
// Usage: open browser console → __wtCelebrate()
(window as any).__wtCelebrate = () => {
  showCelebration.value = true;
  console.log('[__wtCelebrate] Celebration screen opened');
};

// ── Dev helper: reset & regenerate walkthrough forms ──
// Usage: open browser console → __wtResetForms()
(window as any).__wtResetForms = async () => {
  const projectId = store.projectId;
  if (!projectId) {
    console.error('[__wtResetForms] No project ID found in route');
    return;
  }
  try {
    // Delete ALL forms for the tenant first
    const allForms = await getLeadForms(projectId);
    console.log(`[__wtResetForms] Deleting all ${allForms.length} forms…`);
    await Promise.all(allForms.map((f) => deleteLeadForm(projectId, f.id)));
    console.log(
      '[__wtResetForms] All forms deleted. Regenerating walkthrough forms…'
    );
    const result = await resetWalkthroughForms(projectId);
    store.walkthroughFormsGenerated = true;
    console.log(
      `[__wtResetForms] Done! ${result.forms?.length || 0} forms regenerated.`,
      result
    );
  } catch (err) {
    console.error('[__wtResetForms] Failed:', err);
  }
};

onUnmounted(() => {
  delete (window as any).__wtReplay;
  delete (window as any).__wtCelebrate;
  delete (window as any).__wtResetForms;
  window.removeEventListener('keydown', handleKeydown);
  window.removeEventListener('resize', handleResize);
  window.removeEventListener('message', handlePickerMessage);
  window.removeEventListener(
    'walkthrough-session-ids-captured',
    handleSessionIdsCaptured
  );
  document.removeEventListener('click', handleDocumentClick, true);
  if (modalObserver) {
    modalObserver.disconnect();
    modalObserver = null;
  }
  document.body.classList.remove('walkthrough-active');
  stopShowcaseAnimation();
  stopLfCycle();
  stopSpotlightTracking();
  cleanupInteractive();
  disableScrollLock();
  simRef.value?.reset();
});

// Toggle body class so the app navbar can be z-boosted above the overlay
function syncWalkthroughBodyClass(active: boolean) {
  if (active) {
    document.body.classList.add('walkthrough-active');
  } else {
    document.body.classList.remove('walkthrough-active');
    disableScrollLock();
  }
}

// When walkthrough becomes active, init
watch(
  () => store.isActive,
  async (active) => {
    syncWalkthroughBodyClass(active);
    if (active) {
      await navigateIfNeeded();
      await executeBeforeShow();
      await updateSpotlight();
      if (
        store.currentStep?.showcase === 'simulate' ||
        store.currentStep?.showcase === 'integrations' ||
        store.currentStep?.showcase === 'welcome' ||
        store.currentStep?.showcase === 'forms'
      )
        startShowcaseAnimation();
    } else {
      stopShowcaseAnimation();
      stopLfCycle();
      simRef.value?.reset();
    }
  },
  { immediate: true }
);

// When targetUserId becomes available (sandbox user generated), resume navigation
watch(
  () => store.targetUserId,
  async (newId) => {
    if (!store.isActive || !newId) return;
    // If we were waiting for user generation, now we can proceed
    await navigateIfNeeded();
    await executeBeforeShow();
    await updateSpotlight();
  }
);
</script>

<style scoped>
/* ══════════════════════════════════
   WRAPPER (transition root, holds modifier classes)
   ══════════════════════════════════ */
.walkthrough-wrapper {
  /* No z-index here — children set their own z-index at the root
     stacking context level so the tooltip (10003) paints above
     the interactive target (10001) which paints above the
     overlay backdrop (10000). */
}

/* ══════════════════════════════════
   OVERLAY (backdrop layer only)
   ══════════════════════════════════ */
.walkthrough-overlay {
  position: fixed;
  inset: 0;
  z-index: 10000;
  pointer-events: auto;
}

.click-through-active .walkthrough-overlay {
  pointer-events: none;
}

.click-through-active .walkthrough-progress-bar,
.click-through-active .walkthrough-tooltip {
  pointer-events: auto;
}

/* Interactive mode: overlay allows pointer events through so the
   boosted target element (z-index 10001) is directly clickable
   and the timeline remains scrollable beneath the dark mask. */
.interactive-active .walkthrough-overlay {
  pointer-events: none;
}

.interactive-active .walkthrough-progress-bar,
.offtrack-active .walkthrough-overlay {
  pointer-events: none !important;
  background: transparent !important;
}

.offtrack-active .walkthrough-backdrop {
  display: none;
}

.interactive-active .walkthrough-tooltip {
  pointer-events: auto;
}

.walkthrough-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(2px);
}

.walkthrough-spotlight {
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

/* ══════════════════════════════════
   PULSE RING
   ══════════════════════════════════ */
.pulse-ring {
  border-radius: 12px;
  border: 2px solid rgba(99, 102, 241, 0.6);
  animation: pulse-anim 2s ease-in-out infinite;
  pointer-events: none;
  z-index: 10001;
}

@keyframes pulse-anim {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.4;
    transform: scale(1.04);
  }
}

/* ══════════════════════════════════
   PROGRESS BAR
   ══════════════════════════════════ */
.walkthrough-progress-bar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10002;
  padding: 0;
  pointer-events: auto;
  background: rgba(17, 17, 30, 0.92);
  backdrop-filter: blur(8px);
}

.progress-track {
  height: 4px;
  background: rgba(255, 255, 255, 0.15);
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #6366f1, #a855f7, #ec4899);
  transition: width 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
  border-radius: 0 4px 4px 0;
  box-shadow: 0 0 12px rgba(99, 102, 241, 0.5);
}

.progress-info {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 8px 16px;
  gap: 12px;
}

.progress-groups {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.group-pill {
  font-size: 11px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.5);
  transition: all 0.3s ease;
  letter-spacing: 0.02em;
  cursor: pointer;
  user-select: none;
}

.group-pill:hover {
  background: rgba(255, 255, 255, 0.15);
}

.group-pill.active {
  background: rgba(99, 102, 241, 0.3);
  color: white;
  box-shadow: 0 0 8px rgba(99, 102, 241, 0.3);
}

.group-pill.done {
  background: rgba(34, 197, 94, 0.2);
  color: rgba(34, 197, 94, 0.8);
}

.progress-step-count {
  font-size: 13px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.75);
  margin-left: auto;
  white-space: nowrap;
  letter-spacing: 0.02em;
}

/* ══════════════════════════════════
   TOOLTIP
   ══════════════════════════════════ */
.walkthrough-tooltip {
  position: fixed;
  width: 440px;
  max-width: calc(100vw - 24px);
  max-height: calc(100vh - 24px);
  overflow-y: auto;
  background: white;
  border-radius: 18px;
  padding: 32px 34px;
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.12), 0 8px 24px rgba(0, 0, 0, 0.08),
    0 0 0 1px rgba(0, 0, 0, 0.04);
  z-index: 10003;
  pointer-events: auto;
  outline: none;
}

.walkthrough-tooltip::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #6366f1, #a855f7, #6366f1);
  background-size: 200% 100%;
  animation: wt-accent-shimmer 3s ease infinite;
}

@keyframes wt-accent-shimmer {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

.walkthrough-tooltip.is-center {
  text-align: center;
}

.walkthrough-tooltip.is-compact {
  width: auto;
  max-width: calc(100vw - 24px);
  padding: 10px 16px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 10px;
  white-space: nowrap;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.04);
}

.is-compact .tooltip-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  font-size: 16px;
  margin-bottom: 0;
  flex-shrink: 0;
}

.is-compact .tooltip-content {
  display: flex;
  align-items: baseline;
  gap: 6px;
  flex-shrink: 0;
}

.is-compact .tooltip-title {
  font-size: 14px;
  font-weight: 700;
  margin: 0;
  white-space: nowrap;
}

.is-compact .tooltip-body {
  font-size: 13px;
  line-height: 1;
  white-space: nowrap;
}

.is-compact .tooltip-actions {
  margin-top: 0;
  padding-top: 0;
  border-top: none;
  flex-shrink: 0;
}

.is-compact .tooltip-btn-next {
  padding: 6px 14px;
  font-size: 13px;
}

.is-compact .tooltip-btn-back {
  padding: 6px 10px;
  font-size: 13px;
}

.is-compact .tooltip-btn-skip {
  font-size: 11px;
}

.is-compact .tooltip-counter {
  display: none;
}

.tooltip-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: linear-gradient(135deg, #eef2ff, #f5f3ff);
  border: 1px solid #e0e7ff;
  font-size: 26px;
  margin-bottom: 14px;
  line-height: 1;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.08);
}

.is-center .tooltip-icon {
  width: 64px;
  height: 64px;
  border-radius: 18px;
  font-size: 32px;
  margin-bottom: 18px;
}

/* ══════════════════════════════════
   INTEGRATIONS SHOWCASE
   ══════════════════════════════════ */
.ig-showcase {
  width: 100%;
  position: relative;
}

.ig-hero {
  margin-bottom: 32px;
  opacity: 0;
  transform: translateY(16px);
  transition: all 0.36s cubic-bezier(0.22, 1, 0.36, 1);
}
.ig-hero.sc-visible {
  opacity: 1;
  transform: translateY(0);
}

.ig-title {
  font-size: 26px;
  font-weight: 800;
  color: #0f172a;
  margin: 0 0 10px;
  line-height: 1.25;
  letter-spacing: -0.02em;
}

.ig-subtitle {
  font-size: 16px;
  color: #475569;
  margin: 0;
  line-height: 1.55;
}

/* Service rows */
.ig-services {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 24px;
}

.ig-service {
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 18px 22px;
  border-radius: 16px;
  background: #f8fafc;
  border: 1.5px solid #e2e8f0;
  opacity: 0;
  transform: translateX(-20px);
  transition: all 0.36s cubic-bezier(0.22, 1, 0.36, 1);
}
.ig-service.sc-visible {
  opacity: 1;
  transform: translateX(0);
}

.ig-service-logo {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: white;
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.ig-logo-img {
  width: 32px;
  height: 32px;
  object-fit: contain;
}

.ig-service-info {
  flex: 1;
  min-width: 0;
}

.ig-service-name {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 3px;
}

.ig-service-desc {
  font-size: 15px;
  color: #64748b;
  margin: 0;
  line-height: 1.45;
}

/* Note */
.ig-note {
  text-align: center;
  font-size: 14px;
  color: #94a3b8;
  font-weight: 500;
  font-style: italic;
  opacity: 0;
  transform: translateY(8px);
  transition: all 0.3s ease;
}
.ig-note.sc-visible {
  opacity: 1;
  transform: translateY(0);
}

/* ══════════════════════════════════
   FORMS (LEAD CAPTURE) SHOWCASE
   ══════════════════════════════════ */
.lf-showcase {
  width: 100%;
  position: relative;
}

/* ── Badge ── */
.lf-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 12px 4px 8px;
  border-radius: 100px;
  background: linear-gradient(135deg, #eef2ff, #f5f3ff);
  border: 1px solid #e0e7ff;
  font-size: 11px;
  font-weight: 700;
  color: #6366f1;
  letter-spacing: 0.03em;
  margin-bottom: 12px;
}
.lf-badge svg {
  color: #818cf8;
}

/* ── Hero ── */
.lf-hero {
  text-align: center;
  margin-bottom: 18px;
  opacity: 0;
  transform: translateY(16px);
  transition: all 0.4s cubic-bezier(0.22, 1, 0.36, 1);
}
.lf-hero.sc-visible {
  opacity: 1;
  transform: translateY(0);
}

.lf-title {
  font-size: 26px;
  font-weight: 800;
  color: #0f172a;
  margin: 0 0 8px;
  line-height: 1.15;
  letter-spacing: -0.03em;
}

.lf-subtitle {
  font-size: 13.5px;
  color: #64748b;
  margin: 0 auto;
  line-height: 1.55;
  max-width: 420px;
}

/* ── Type switcher tabs ── */
.lf-tabs {
  display: flex;
  justify-content: center;
  gap: 2px;
  margin-bottom: 16px;
  background: #f1f5f9;
  border-radius: 14px;
  padding: 3px;
  width: fit-content;
  margin-left: auto;
  margin-right: auto;
  opacity: 0;
  transform: translateY(10px);
  transition: all 0.35s cubic-bezier(0.22, 1, 0.36, 1);
}
.lf-tabs.sc-visible {
  opacity: 1;
  transform: translateY(0);
}

.lf-tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  border-radius: 11px;
  font-size: 12.5px;
  font-weight: 600;
  color: #94a3b8;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.22, 1, 0.36, 1);
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  user-select: none;
  position: relative;
}
.lf-tab:hover {
  color: #64748b;
}
.lf-tab-active {
  color: #4f46e5;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04),
    0 0 0 1px rgba(99, 102, 241, 0.08);
}
.lf-tab-active:hover {
  color: #4f46e5;
  background: #fff;
}
.lf-tab svg {
  width: 15px;
  height: 15px;
  flex-shrink: 0;
}

/* ── Stage: browser mockup container ── */
.lf-stage {
  margin-bottom: 14px;
  opacity: 0;
  transform: translateY(14px);
  transition: all 0.45s cubic-bezier(0.22, 1, 0.36, 1) 0.08s;
}
.lf-stage.sc-visible {
  opacity: 1;
  transform: translateY(0);
}

.lf-browser {
  border-radius: 14px;
  overflow: hidden;
  border: 1.5px solid #e2e8f0;
  background: #fff;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06), 0 2px 6px rgba(0, 0, 0, 0.03),
    0 0 0 1px rgba(0, 0, 0, 0.02);
}

.lf-browser-bar {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 8px 14px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.lf-bar-icons {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

.lf-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}
.lf-dot-r {
  background: #fca5a5;
}
.lf-dot-y {
  background: #fcd34d;
}
.lf-dot-g {
  background: #86efac;
}

.lf-url-pill {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-left: 10px;
  font-size: 10.5px;
  font-weight: 500;
  color: #94a3b8;
  background: #f1f5f9;
  padding: 3px 12px;
  border-radius: 6px;
}

/* ── Viewport (storefront + form overlays) ── */
.lf-viewport {
  position: relative;
  padding: 0;
  background: #fff;
  min-height: 220px;
  overflow: hidden;
}

.lf-sf-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 18px;
  border-bottom: 1px solid #f1f5f9;
}

.lf-sf-logo {
  width: 56px;
  height: 10px;
  border-radius: 4px;
  background: linear-gradient(90deg, #94a3b8, #cbd5e1);
}

.lf-sf-menu {
  display: flex;
  gap: 10px;
}
.lf-sf-menu span {
  width: 26px;
  height: 5px;
  border-radius: 3px;
  background: #e2e8f0;
  display: block;
}

/* Hero banner in storefront */
.lf-sf-hero-banner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 14px 18px;
  background: linear-gradient(135deg, #eef2ff 0%, #faf5ff 50%, #fdf2f8 100%);
  border-bottom: 1px solid #f1f5f9;
}
.lf-sf-hero-text {
  width: 120px;
  height: 8px;
  border-radius: 4px;
  background: linear-gradient(90deg, #a5b4fc, #c4b5fd);
}
.lf-sf-hero-sub {
  width: 80px;
  height: 5px;
  border-radius: 3px;
  background: #c7d2fe;
  opacity: 0.6;
}
.lf-sf-hero-btn {
  width: 56px;
  height: 16px;
  border-radius: 8px;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  margin-top: 2px;
}

.lf-sf-section-label {
  width: 60px;
  height: 5px;
  border-radius: 3px;
  background: #e2e8f0;
  margin: 12px 18px 10px;
}

.lf-sf-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  padding: 0 18px 16px;
}

.lf-sf-card {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.lf-sf-card-hide {
  display: flex;
}

.lf-sf-img {
  aspect-ratio: 4 / 3;
  border-radius: 6px;
}
/* Product color variants */
.lf-sf-img-1 {
  background: linear-gradient(145deg, #fef3c7 0%, #fde68a 100%);
}
.lf-sf-img-2 {
  background: linear-gradient(145deg, #dbeafe 0%, #bfdbfe 100%);
}
.lf-sf-img-3 {
  background: linear-gradient(145deg, #fce7f3 0%, #fbcfe8 100%);
}
.lf-sf-img-4 {
  background: linear-gradient(145deg, #d1fae5 0%, #a7f3d0 100%);
}

.lf-sf-line {
  height: 5px;
  border-radius: 2.5px;
  background: #e2e8f0;
  width: 80%;
}

.lf-sf-price {
  height: 5px;
  border-radius: 2.5px;
  background: #edf0f4;
  width: 40%;
}

/* ── Dimmer overlay ── */
.lf-dimmer {
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.28);
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.45s ease;
}
.lf-dim-on {
  opacity: 1;
}

/* ── Form mockup shared ── */
.lf-mock {
  position: absolute;
  background: #fff;
  text-align: center;
  z-index: 2;
  opacity: 0;
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.lf-mock.sc-visible {
  opacity: 1;
}

.lf-mock-x {
  position: absolute;
  top: 8px;
  right: 10px;
  font-size: 10px;
  color: #cbd5e1;
  cursor: default;
  line-height: 1;
  z-index: 1;
}

.lf-mock-body {
  position: relative;
}

.lf-mock-icon {
  font-size: 24px;
  line-height: 1;
  margin-bottom: 6px;
}

.lf-mock-h {
  font-size: 13px;
  font-weight: 800;
  color: #0f172a;
  margin-bottom: 2px;
  letter-spacing: -0.01em;
}
.lf-mock-h-sm {
  font-size: 11.5px;
  margin-bottom: 0;
}

.lf-mock-p {
  font-size: 9px;
  color: #94a3b8;
  margin-bottom: 10px;
  line-height: 1.3;
}

.lf-field-icon {
  flex-shrink: 0;
  opacity: 0.5;
}

.lf-mock-field {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 7px 10px;
  border-radius: 8px;
  border: 1.2px solid #e2e8f0;
  background: #f8fafc;
  margin-bottom: 7px;
}
.lf-mock-field span {
  font-size: 9.5px;
  color: #94a3b8;
}
.lf-mock-field-inline {
  flex: 1;
  margin-bottom: 0;
  padding: 5px 8px;
}

.lf-mock-cta {
  padding: 7px 14px;
  border-radius: 8px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.3);
}
.lf-mock-cta-green {
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  box-shadow: 0 2px 8px rgba(34, 197, 94, 0.3);
}
.lf-mock-cta-dark {
  background: #0f172a;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.2);
  padding: 5px 12px;
  font-size: 9.5px;
  border-radius: 6px;
}

/* ── Color accent strip at top of popup ── */
.lf-mock-accent {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  border-radius: 16px 16px 0 0;
}
.lf-mock-accent-purple {
  background: linear-gradient(90deg, #6366f1, #a855f7, #ec4899);
}

/* ── POPUP style ── */
.lf-mock-popup {
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) scale(0.8);
  width: 190px;
  border-radius: 16px;
  padding: 0;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(99, 102, 241, 0.18),
    0 8px 20px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(99, 102, 241, 0.06);
}
.lf-mock-popup.sc-visible {
  transform: translate(-50%, -50%) scale(1);
}
.lf-mock-popup .lf-mock-body {
  padding: 16px 16px 14px;
}

/* ── SLIDE-IN style ── */
.lf-mock-slide {
  bottom: 12px;
  right: 12px;
  transform: translateY(20px) scale(0.9);
  width: 180px;
  border-radius: 16px;
  padding: 0;
  overflow: hidden;
  box-shadow: 0 16px 48px rgba(34, 197, 94, 0.14),
    0 6px 16px rgba(0, 0, 0, 0.07), 0 0 0 1px rgba(0, 0, 0, 0.03);
  text-align: left;
}
.lf-mock-slide.sc-visible {
  transform: translateY(0) scale(1);
}

.lf-slide-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: linear-gradient(135deg, #22c55e, #16a34a);
}
.lf-slide-avatar {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  flex-shrink: 0;
}
.lf-slide-info {
  flex: 1;
  min-width: 0;
}
.lf-slide-info .lf-mock-h-sm {
  color: #fff;
  font-size: 11px;
  margin-bottom: 1px;
}
.lf-slide-status {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 8.5px;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 500;
}
.lf-online-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #bbf7d0;
  display: inline-block;
  animation: lf-pulse-dot 2s ease-in-out infinite;
}
@keyframes lf-pulse-dot {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
}
.lf-mock-x-slide {
  position: static;
  color: rgba(255, 255, 255, 0.6);
  font-size: 10px;
  cursor: default;
}

.lf-slide-body {
  padding: 12px;
}
.lf-slide-bubble {
  background: #f0fdf4;
  border: 1px solid #dcfce7;
  border-radius: 12px 12px 12px 2px;
  padding: 8px 10px;
  font-size: 10px;
  color: #166534;
  font-weight: 600;
  line-height: 1.4;
  margin-bottom: 10px;
}

/* ── BANNER style ── */
.lf-mock-banner {
  top: 0;
  left: 0;
  right: 0;
  transform: translateY(-100%);
  border-radius: 0;
  padding: 8px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: linear-gradient(90deg, #0f172a 0%, #1e293b 100%);
  text-align: left;
}
.lf-mock-banner.sc-visible {
  transform: translateY(0);
}

.lf-banner-left {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}
.lf-banner-emoji {
  font-size: 14px;
  flex-shrink: 0;
}
.lf-banner-left strong {
  font-size: 10.5px;
  font-weight: 800;
  color: #fff;
  white-space: nowrap;
}
.lf-banner-sub {
  font-size: 9px;
  color: rgba(255, 255, 255, 0.5);
  white-space: nowrap;
}

.lf-banner-right {
  display: flex;
  gap: 6px;
  align-items: center;
  flex-shrink: 0;
}
.lf-mock-banner .lf-mock-field-inline {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.15);
}
.lf-mock-banner .lf-mock-field-inline span {
  color: rgba(255, 255, 255, 0.4);
}
.lf-mock-banner .lf-mock-cta-dark {
  background: #fff;
  color: #0f172a;
  font-weight: 800;
  box-shadow: none;
}

/* ── Morph transition between form types ── */
.lf-morph-enter-active {
  transition: all 0.42s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.lf-morph-leave-active {
  transition: all 0.18s ease-in;
}
.lf-morph-enter-from {
  opacity: 0;
  transform: scale(0.8);
}
.lf-morph-leave-to {
  opacity: 0;
  transform: scale(0.92);
}
/* Override for positioned mocks */
.lf-morph-enter-from.lf-mock-popup {
  transform: translate(-50%, -50%) scale(0.8);
}
.lf-morph-leave-to.lf-mock-popup {
  transform: translate(-50%, -50%) scale(0.92);
}
.lf-morph-enter-from.lf-mock-slide {
  transform: translateY(20px) scale(0.9);
}
.lf-morph-leave-to.lf-mock-slide {
  transform: translateY(10px) scale(0.92);
}
.lf-morph-enter-from.lf-mock-banner {
  transform: translateY(-100%);
}
.lf-morph-leave-to.lf-mock-banner {
  transform: translateY(-100%);
}

/* ── Bottom feature chips ── */
.lf-features {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex-wrap: wrap;
  opacity: 0;
  transform: translateY(10px);
  transition: all 0.35s cubic-bezier(0.22, 1, 0.36, 1);
}
.lf-features.sc-visible {
  opacity: 1;
  transform: translateY(0);
}

.lf-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 100px;
  background: #f8fafc;
  border: 1px solid #f1f5f9;
  font-size: 11px;
  font-weight: 600;
  color: #475569;
}
.lf-chip-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}
.lf-chip-dot-blue {
  background: #6366f1;
}
.lf-chip-dot-purple {
  background: #a855f7;
}
.lf-chip-dot-green {
  background: #22c55e;
}

/* ── Generating spinner ── */
.lf-generating {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 12px 16px;
  margin-top: 14px;
  border-radius: 12px;
  background: #f5f3ff;
  border: 1px solid #ede9fe;
  font-size: 13px;
  font-weight: 500;
  color: #6366f1;
  opacity: 0;
  transition: opacity 0.3s ease;
}
.lf-generating.sc-visible {
  opacity: 1;
}

.lf-gen-spinner {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(99, 102, 241, 0.2);
  border-top-color: #6366f1;
  border-radius: 50%;
  animation: wt-spin 0.7s linear infinite;
}

/* ══════════════════════════════════
   FORMS PICKER SHOWCASE
   ══════════════════════════════════ */
.fp-showcase {
  width: 100%;
  position: relative;
  overflow: hidden;
}

/* ── Hero ── */
.fp-hero {
  text-align: center;
  margin-bottom: 22px;
  opacity: 0;
  transform: translateY(18px);
  transition: all 0.5s cubic-bezier(0.22, 1, 0.36, 1);
}
.fp-hero.sc-visible {
  opacity: 1;
  transform: translateY(0);
}

.fp-title {
  font-size: 23px;
  font-weight: 800;
  color: #1e1b4b;
  letter-spacing: -0.5px;
  line-height: 1.25;
  margin: 0 0 8px;
}
.fp-subtitle {
  font-size: 13.5px;
  color: #64748b;
  line-height: 1.55;
  max-width: 360px;
  margin: 0 auto;
}

/* ── A / B comparison visual ── */
.fp-compare {
  margin: 0 auto 22px;
  max-width: 360px;
  opacity: 0;
  transform: scale(0.92) translateY(14px);
  transition: all 0.55s cubic-bezier(0.22, 1, 0.36, 1) 0.05s;
}
.fp-compare.sc-visible {
  opacity: 1;
  transform: scale(1) translateY(0);
}

.fp-compare-inner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.fp-side {
  flex: 1;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.fp-side-badge {
  width: 26px;
  height: 26px;
  border-radius: 8px;
  background: linear-gradient(135deg, #7c3aed, #6d28d9);
  color: white;
  font-size: 11px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(124, 58, 237, 0.25);
  animation: fp-badge-pulse 2.5s ease-in-out infinite;
}
.fp-side-badge-b {
  background: linear-gradient(135deg, #f59e0b, #d97706);
  box-shadow: 0 2px 8px rgba(245, 158, 11, 0.25);
  animation-delay: 1.25s;
}

@keyframes fp-badge-pulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

/* ── Mini form mockups ── */
.fp-mini-form {
  width: 100%;
  max-width: 140px;
  padding: 14px 12px;
  border-radius: 12px;
  background: white;
  border: 1px solid #e5e7eb;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;
}

.fp-mini-a {
  animation: fp-float-a 4s ease-in-out infinite;
}
.fp-mini-b {
  animation: fp-float-b 4s ease-in-out infinite;
  background: #1e1b4b;
  border-color: #312e81;
}

@keyframes fp-float-a {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-4px);
  }
}
@keyframes fp-float-b {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-4px);
  }
}

.fp-mini-accent {
  height: 4px;
  border-radius: 2px;
  width: 60%;
  background: linear-gradient(90deg, #7c3aed, #a78bfa);
  margin-bottom: 10px;
}
.fp-mini-accent-dark {
  background: linear-gradient(90deg, #f59e0b, #fbbf24);
}

.fp-mini-heading {
  height: 8px;
  border-radius: 4px;
  width: 80%;
  background: #e2e8f0;
  margin-bottom: 6px;
}
.fp-mini-h-dark {
  background: #4338ca;
}

.fp-mini-sub {
  height: 5px;
  border-radius: 2px;
  width: 55%;
  background: #f1f5f9;
  margin-bottom: 10px;
}
.fp-mini-s-dark {
  background: #3730a3;
}

.fp-mini-field {
  height: 22px;
  border-radius: 6px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  margin-bottom: 8px;
}
.fp-mini-f-dark {
  background: #312e81;
  border-color: #4338ca;
}

.fp-mini-btn {
  height: 22px;
  border-radius: 6px;
  background: linear-gradient(135deg, #7c3aed, #6d28d9);
  box-shadow: 0 2px 6px rgba(124, 58, 237, 0.25);
}
.fp-mini-btn-warm {
  background: linear-gradient(135deg, #f59e0b, #d97706);
  box-shadow: 0 2px 6px rgba(245, 158, 11, 0.25);
}

/* ── VS divider ── */
.fp-vs {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
  flex-shrink: 0;
}
.fp-vs-ring {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: linear-gradient(135deg, #f5f3ff, #ede9fe);
  border: 2px solid #ddd6fe;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 2;
  animation: fp-vs-glow 3s ease-in-out infinite;
}
.fp-vs-ring span {
  font-size: 10px;
  font-weight: 900;
  color: #7c3aed;
  letter-spacing: 0.5px;
}
.fp-vs-line {
  width: 2px;
  height: 24px;
  background: linear-gradient(180deg, #ddd6fe, transparent);
  margin-top: -2px;
}

@keyframes fp-vs-glow {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(124, 58, 237, 0);
  }
  50% {
    box-shadow: 0 0 12px 4px rgba(124, 58, 237, 0.12);
  }
}

/* ── Type cards row ── */
.fp-types {
  display: flex;
  gap: 10px;
  justify-content: center;
  margin-bottom: 20px;
  opacity: 0;
  transform: translateY(14px);
  transition: all 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.1s;
}
.fp-types.sc-visible {
  opacity: 1;
  transform: translateY(0);
}

.fp-type-card {
  flex: 1;
  max-width: 120px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 14px 10px 12px;
  text-align: center;
  position: relative;
  overflow: hidden;
  transition: all 0.25s cubic-bezier(0.22, 1, 0.36, 1);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}
.fp-type-card:hover {
  border-color: #c4b5fd;
  transform: translateY(-3px);
  box-shadow: 0 8px 20px rgba(124, 58, 237, 0.1);
}

.fp-type-glow {
  position: absolute;
  top: -40%;
  left: -40%;
  width: 180%;
  height: 180%;
  background: radial-gradient(
    circle,
    rgba(124, 58, 237, 0.05) 0%,
    transparent 70%
  );
  opacity: 0;
  transition: opacity 0.3s;
  pointer-events: none;
}
.fp-type-card:hover .fp-type-glow {
  opacity: 1;
}

.fp-type-icon {
  color: #7c3aed;
  margin-bottom: 6px;
  display: flex;
  justify-content: center;
}

.fp-type-name {
  font-size: 12px;
  font-weight: 700;
  color: #1e1b4b;
  margin-bottom: 6px;
}

.fp-type-ab {
  display: flex;
  justify-content: center;
  gap: 4px;
}

.fp-ab {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 6px;
  font-size: 9px;
  font-weight: 800;
}
.fp-ab-a {
  background: linear-gradient(135deg, #ede9fe, #ddd6fe);
  color: #7c3aed;
}
.fp-ab-b {
  background: linear-gradient(135deg, #fef3c7, #fde68a);
  color: #b45309;
}

/* ── Flow stepper ── */
.fp-flow {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-bottom: 6px;
  opacity: 0;
  transform: translateY(14px);
  transition: all 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.2s;
}
.fp-flow.sc-visible {
  opacity: 1;
  transform: translateY(0);
}

.fp-flow-step {
  display: flex;
  align-items: center;
  gap: 6px;
}

.fp-flow-num {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: linear-gradient(135deg, #7c3aed, #6d28d9);
  color: white;
  font-size: 10px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(124, 58, 237, 0.2);
}
.fp-flow-num-2 {
  background: linear-gradient(135deg, #8b5cf6, #7c3aed);
}
.fp-flow-num-3 {
  background: linear-gradient(135deg, #a78bfa, #8b5cf6);
}

.fp-flow-text {
  display: flex;
  flex-direction: column;
}
.fp-flow-text strong {
  font-size: 11px;
  font-weight: 700;
  color: #1e1b4b;
  line-height: 1.2;
}
.fp-flow-text span {
  font-size: 10px;
  color: #94a3b8;
  line-height: 1.3;
}

.fp-flow-connector {
  opacity: 0.5;
  flex-shrink: 0;
}

/* ── Loading ── */
.fp-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 14px;
  padding: 10px 16px;
  border-radius: 12px;
  background: #f5f3ff;
  border: 1px solid #ede9fe;
  font-size: 12px;
  font-weight: 500;
  color: #6366f1;
  opacity: 0;
  transition: opacity 0.3s;
}
.fp-loading.sc-visible {
  opacity: 1;
}

.fp-skip-section {
  margin-top: 18px;
  text-align: center;
  opacity: 0;
  transition: opacity 0.4s;
}
.fp-skip-section.sc-visible {
  opacity: 1;
}
.fp-skip-btn {
  display: inline-block;
  padding: 10px 28px;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  background: #f8fafc;
  color: #64748b;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s, color 0.2s, border-color 0.2s;
}
.fp-skip-btn:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
  color: #475569;
}
.fp-skip-hint {
  margin: 6px 0 0;
  font-size: 12px;
  color: #94a3b8;
}

/* ══════════════════════════════════
   WELCOME SHOWCASE
   ══════════════════════════════════ */
.welcome-showcase {
  text-align: center;
}

/* ── Animated check ── */
.wc-check-wrap {
  margin-bottom: 20px;
}

.wc-check-svg {
  width: 60px;
  height: 60px;
}

.wc-check-bg {
  fill: none;
  stroke: #e0e7ff;
  stroke-width: 2;
}

.wc-check-ring {
  fill: none;
  stroke: #6366f1;
  stroke-width: 2.5;
  stroke-linecap: round;
  stroke-dasharray: 182;
  stroke-dashoffset: 182;
  animation: wc-draw-ring 0.7s 0.15s ease forwards;
  transform-origin: center;
}

.wc-check-tick {
  fill: none;
  stroke: #6366f1;
  stroke-width: 3;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-dasharray: 36;
  stroke-dashoffset: 36;
  animation: wc-draw-tick 0.35s 0.7s ease forwards;
}

@keyframes wc-draw-ring {
  to {
    stroke-dashoffset: 0;
  }
}

@keyframes wc-draw-tick {
  to {
    stroke-dashoffset: 0;
  }
}

/* ── Demo badge ── */
.wc-demo-badge {
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  color: #6366f1;
  background: #eef2ff;
  border: 1px solid #e0e7ff;
  padding: 4px 14px;
  border-radius: 20px;
  margin-bottom: 14px;
  opacity: 0;
  animation: wc-fade-up 0.4s 0.2s ease forwards;
}

/* ── Title ── */
.wc-title {
  font-size: clamp(24px, 3vw, 32px);
  font-weight: 800;
  color: #111827;
  margin: 0 0 8px;
  line-height: 1.2;
  letter-spacing: -0.025em;
  opacity: 0;
  animation: wc-fade-up 0.5s 0.3s ease forwards;
}

.wc-title-em {
  color: #6366f1;
}

.wc-subtitle {
  font-size: 15px;
  color: #6b7280;
  margin: 0 0 24px;
  line-height: 1.55;
  max-width: 340px;
  margin-left: auto;
  margin-right: auto;
  opacity: 0;
  animation: wc-fade-up 0.5s 0.45s ease forwards;
}

@keyframes wc-fade-up {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ── Pipeline ── */
.wc-pipeline {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  margin-bottom: 24px;
  padding: 16px 0;
  opacity: 0;
  animation: wc-fade-up 0.5s 0.6s ease forwards;
}

.wc-step {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 12px;
  background: #f9fafb;
  border: 1px solid #f3f4f6;
  position: relative;
  opacity: 0;
  animation: wc-step-pop 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
}

.wc-s1 {
  animation-delay: 0.7s;
}
.wc-s2 {
  animation-delay: 1s;
}
.wc-s3 {
  animation-delay: 1.3s;
}

@keyframes wc-step-pop {
  from {
    opacity: 0;
    transform: scale(0.85) translateY(6px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.wc-step-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  animation: wc-dot-pulse 2s ease-in-out infinite;
}

.wc-dot-blue {
  background: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
}
.wc-dot-purple {
  background: #8b5cf6;
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.15);
}
.wc-dot-green {
  background: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.15);
}

@keyframes wc-dot-pulse {
  0%,
  100% {
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(99, 102, 241, 0.05);
  }
}

.wc-step-content {
  display: flex;
  align-items: center;
  gap: 6px;
}

.wc-step-emoji {
  font-size: 18px;
  line-height: 1;
}

.wc-step-name {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  white-space: nowrap;
}

/* ── Connectors ── */
.wc-connector {
  width: 32px;
  height: 2px;
  position: relative;
  opacity: 0;
  animation: wc-conn-in 0.3s ease forwards;
}

.wc-c1 {
  animation-delay: 0.85s;
}
.wc-c2 {
  animation-delay: 1.15s;
}

@keyframes wc-conn-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.wc-conn-line {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: #e5e7eb;
  border-radius: 1px;
}

.wc-conn-pulse {
  position: absolute;
  top: -1px;
  width: 8px;
  height: 4px;
  border-radius: 2px;
  background: #6366f1;
  animation: wc-pulse-move 1.8s ease-in-out infinite;
}

@keyframes wc-pulse-move {
  0% {
    left: 0;
    opacity: 0;
  }
  20% {
    opacity: 1;
  }
  80% {
    opacity: 1;
  }
  100% {
    left: calc(100% - 8px);
    opacity: 0;
  }
}

/* ── Footer meta ── */
.wc-meta {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  opacity: 0;
  animation: wc-fade-up 0.4s 1.5s ease forwards;
}

.wc-meta-pill {
  font-size: 12px;
  font-weight: 500;
  color: #9ca3af;
  background: #f9fafb;
  border: 1px solid #f3f4f6;
  padding: 4px 10px;
  border-radius: 20px;
}

.tooltip-title {
  font-size: 22px;
  font-weight: 800;
  color: #111827;
  margin: 0 0 8px 0;
  line-height: 1.25;
  letter-spacing: -0.02em;
}

.tooltip-body {
  font-size: 15px;
  color: #6b7280;
  margin: 0;
  line-height: 1.6;
}

/* Drag objective pill */
.drag-objective {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 14px;
  padding: 10px 14px;
  border-radius: 10px;
  background: #f9fafb;
  border: 1.5px dashed #d1d5db;
  transition: all 0.4s ease;
}

.drag-objective.objective-complete {
  background: #ecfdf5;
  border: 1.5px solid #34d399;
}

.objective-check {
  font-size: 16px;
  flex-shrink: 0;
}

.objective-text {
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
  transition: color 0.3s;
}

.objective-complete .objective-text {
  color: #059669;
}

.tooltip-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #f3f4f6;
}

.is-center .tooltip-actions {
  justify-content: center;
}

.tooltip-btn {
  border: none;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  border-radius: 10px;
  transition: all 0.2s ease;
}

.tooltip-btn:active {
  transform: scale(0.96);
}

.tooltip-btn-next {
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: white;
  padding: 10px 24px;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.25);
}

.tooltip-btn-next:hover {
  box-shadow: 0 4px 16px rgba(99, 102, 241, 0.4);
  transform: translateY(-1px);
}

.tooltip-btn-next.tooltip-btn-locked {
  background: #e2e8f0;
  color: #94a3b8;
  cursor: not-allowed;
  box-shadow: none;
  transform: none;
}

.tooltip-btn-next.tooltip-btn-locked:hover {
  box-shadow: none;
  transform: none;
}

.tooltip-btn-back {
  background: #f3f4f6;
  color: #6b7280;
  padding: 10px 14px;
  flex-shrink: 0;
}

.tooltip-btn-back:hover {
  background: #e5e7eb;
  color: #374151;
}

.tooltip-btn-skip {
  background: none;
  color: #94a3b8;
  padding: 10px 8px;
  font-size: 12px;
}

/* Push Back+Next (or just Next) to the right */
.tooltip-btn-skip + .tooltip-btn-back,
.tooltip-btn-skip + .tooltip-btn-next {
  margin-left: auto;
}

.tooltip-btn-skip:hover {
  color: #64748b;
}

.tooltip-btn-skip-step {
  background: none;
  color: #94a3b8;
  padding: 10px 8px;
  font-size: 12px;
  margin-left: auto;
}
.tooltip-btn-skip-step:hover {
  color: #64748b;
}

.tooltip-counter {
  margin-top: 10px;
  font-size: 11px;
  color: #cbd5e1;
  font-weight: 600;
  letter-spacing: 0.06em;
}

/* ══════════════════════════════════
   SHOWCASE: SIMULATE — PREMIUM
   ══════════════════════════════════ */
.walkthrough-tooltip.is-showcase {
  width: 820px;
  max-width: calc(100vw - 24px);
  padding: 40px 44px 32px;
  text-align: center;
  border-radius: 24px;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 25px 60px -12px rgba(0, 0, 0, 0.12),
    0 8px 24px -8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  outline: none;
}

.walkthrough-tooltip.is-showcase::before {
  display: none;
}

/* ── Particles ── */
.sc-particles {
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
}

.sc-particle {
  position: absolute;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  opacity: 0;
  animation: sc-float 8s ease-in-out infinite;
}

.sc-p-1 {
  left: 8%;
  top: 15%;
  background: #818cf8;
  animation-delay: 0s;
}
.sc-p-2 {
  left: 88%;
  top: 20%;
  background: #34d399;
  animation-delay: 1s;
}
.sc-p-3 {
  left: 25%;
  top: 80%;
  background: #fbbf24;
  animation-delay: 2s;
}
.sc-p-4 {
  left: 70%;
  top: 85%;
  background: #f472b6;
  animation-delay: 3s;
}
.sc-p-5 {
  left: 45%;
  top: 10%;
  background: #60a5fa;
  animation-delay: 0.5s;
}
.sc-p-6 {
  left: 92%;
  top: 55%;
  background: #a78bfa;
  animation-delay: 1.5s;
}
.sc-p-7 {
  left: 5%;
  top: 60%;
  background: #34d399;
  animation-delay: 2.5s;
}
.sc-p-8 {
  left: 60%;
  top: 5%;
  background: #fbbf24;
  animation-delay: 3.5s;
}
.sc-p-9 {
  left: 15%;
  top: 40%;
  background: #818cf8;
  animation-delay: 4s;
  width: 4px;
  height: 4px;
}
.sc-p-10 {
  left: 80%;
  top: 45%;
  background: #60a5fa;
  animation-delay: 4.5s;
  width: 4px;
  height: 4px;
}
.sc-p-11 {
  left: 35%;
  top: 90%;
  background: #f472b6;
  animation-delay: 5s;
  width: 4px;
  height: 4px;
}
.sc-p-12 {
  left: 55%;
  top: 70%;
  background: #a78bfa;
  animation-delay: 5.5s;
  width: 4px;
  height: 4px;
}

@keyframes sc-float {
  0%,
  100% {
    opacity: 0;
    transform: translateY(0) scale(0.5);
  }
  20% {
    opacity: 0.6;
    transform: translateY(-12px) scale(1);
  }
  80% {
    opacity: 0.3;
    transform: translateY(-28px) scale(0.8);
  }
}

/* ── SC container ── */
.sc {
  width: 100%;
  position: relative;
}

/* ── Hero header ── */
.sc-hero {
  margin-bottom: 32px;
  opacity: 0;
  transform: translateY(16px);
  transition: all 0.36s cubic-bezier(0.22, 1, 0.36, 1);
}

.sc-hero.sc-visible {
  opacity: 1;
  transform: translateY(0);
}

.sc-badge {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 5px 14px;
  border-radius: 100px;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.12em;
  color: #6366f1;
  background: linear-gradient(135deg, #eef2ff, #e0e7ff);
  border: 1px solid rgba(99, 102, 241, 0.15);
  margin-bottom: 16px;
}

.sc-badge-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #6366f1;
  animation: sc-badge-pulse 2s ease-in-out infinite;
}

@keyframes sc-badge-pulse {
  0%,
  100% {
    opacity: 1;
    box-shadow: 0 0 0 0 rgba(99, 102, 241, 0.4);
  }
  50% {
    opacity: 0.7;
    box-shadow: 0 0 0 6px rgba(99, 102, 241, 0);
  }
}

.sc-hero-title {
  font-size: 28px;
  font-weight: 800;
  color: #0f172a;
  margin: 0 0 10px;
  line-height: 1.25;
  letter-spacing: -0.02em;
}

.sc-gradient-text {
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.sc-hero-sub {
  font-size: 15px;
  color: #475569;
  margin: 0;
  line-height: 1.55;
  max-width: 460px;
  margin-left: auto;
  margin-right: auto;
}

/* ── Pipeline (desktop: 3-col) ── */
.sc-pipeline {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 0;
}

.sc-stage {
  flex: 0 0 210px;
  display: flex;
  flex-direction: column;
  align-items: center;
  opacity: 0;
  transform: translateY(24px) scale(0.9);
  transition: all 0.36s cubic-bezier(0.22, 1, 0.36, 1);
}

.sc-stage.sc-visible {
  opacity: 1;
  transform: translateY(0) scale(1);
}

.sc-stage-msgs {
  flex: 0 0 240px;
}

/* Step number */
.sc-stage-num {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 800;
  color: white;
  margin-bottom: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.sc-num-blue {
  background: linear-gradient(135deg, #3b82f6, #6366f1);
}
.sc-num-purple {
  background: linear-gradient(135deg, #8b5cf6, #a855f7);
}
.sc-num-green {
  background: linear-gradient(135deg, #10b981, #34d399);
}

/* Stage card */
.sc-stage-card {
  width: 100%;
  border-radius: 20px;
  padding: 24px 18px 20px;
  text-align: center;
  position: relative;
  backdrop-filter: blur(8px);
}

.sc-card-blue {
  background: linear-gradient(160deg, #eff6ff 0%, #dbeafe 100%);
  border: 1.5px solid rgba(59, 130, 246, 0.2);
  box-shadow: 0 8px 24px -4px rgba(59, 130, 246, 0.12),
    0 0 0 1px rgba(255, 255, 255, 0.6) inset;
}

.sc-card-purple {
  background: linear-gradient(160deg, #f5f3ff 0%, #ede9fe 100%);
  border: 1.5px solid rgba(139, 92, 246, 0.2);
  box-shadow: 0 8px 24px -4px rgba(139, 92, 246, 0.12),
    0 0 0 1px rgba(255, 255, 255, 0.6) inset;
}

.sc-card-green {
  background: linear-gradient(160deg, #ecfdf5 0%, #d1fae5 100%);
  border: 1.5px solid rgba(16, 185, 129, 0.2);
  box-shadow: 0 8px 24px -4px rgba(16, 185, 129, 0.12),
    0 0 0 1px rgba(255, 255, 255, 0.6) inset;
}

.sc-stage-icon {
  font-size: 34px;
  margin-bottom: 6px;
  line-height: 1;
}

.sc-stage-title {
  font-size: 17px;
  font-weight: 800;
  color: #0f172a;
  margin: 0 0 4px;
  letter-spacing: -0.01em;
}

.sc-stage-desc {
  font-size: 12px;
  color: #64748b;
  margin: 0 0 12px;
  font-weight: 600;
  line-height: 1.3;
}

/* Action list */
.sc-action-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.sc-action-list li {
  display: flex;
  align-items: center;
  gap: 9px;
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  text-align: left;
}

.sc-act-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  display: inline-block;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}

.sc-dot-bg-blue {
  background: #3b82f6;
}
.sc-dot-bg-amber {
  background: #f59e0b;
}
.sc-dot-bg-violet {
  background: #8b5cf6;
}
.sc-dot-bg-green {
  background: #10b981;
}
.sc-dot-bg-red {
  background: #ef4444;
}

/* Brain animation */
.sc-brain {
  position: relative;
  width: 56px;
  height: 56px;
  margin: 0 auto 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sc-brain-ring {
  position: absolute;
  inset: -4px;
  border-radius: 50%;
  border: 2px solid transparent;
  border-top-color: #8b5cf6;
  border-right-color: #a855f7;
  animation: sc-brain-spin 3s linear infinite;
}

.sc-ring-2 {
  inset: -10px;
  border-top-color: transparent;
  border-right-color: transparent;
  border-bottom-color: #c4b5fd;
  border-left-color: #8b5cf6;
  animation-direction: reverse;
  animation-duration: 4s;
  opacity: 0.5;
}

@keyframes sc-brain-spin {
  to {
    transform: rotate(360deg);
  }
}

.sc-brain-emoji {
  font-size: 28px;
  line-height: 1;
  position: relative;
  z-index: 1;
}

/* AI feature list */
.sc-ai-list {
  list-style: none;
  margin: 0;
  padding: 0;
  text-align: left;
}

.sc-ai-list li {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 13px;
  color: #374151;
  padding: 3.5px 0;
  font-weight: 600;
}

.sc-ai-check {
  color: #8b5cf6;
  font-weight: 800;
  font-size: 14px;
  text-shadow: 0 1px 3px rgba(139, 92, 246, 0.3);
}

/* ── Connector chevrons ── */
.sc-connector {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  padding-top: 44px;
  opacity: 0;
  transform: translateX(-4px);
  transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
}

.sc-connector.sc-visible {
  opacity: 1;
  transform: translateX(0);
}

.sc-chevron {
  font-size: 28px;
  font-weight: 300;
  color: #c7d2fe;
  line-height: 1;
  user-select: none;
}

.sc-arrowhead {
  animation: sc-arrow-pulse 2s ease-in-out infinite;
}

@keyframes sc-arrow-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

/* ── Phone mockup messages ── */
.sc-phone-card {
  padding: 16px 14px;
}

.sc-phone {
  border-radius: 14px;
  overflow: hidden;
  margin-bottom: 8px;
  opacity: 0;
  transform: translateY(10px) scale(0.95);
  transition: all 0.27s cubic-bezier(0.22, 1, 0.36, 1);
}

.sc-phone.sc-visible {
  opacity: 1;
  transform: translateY(0) scale(1);
}

/* Email preview card */
.sc-email-card {
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 8px;
  background: white;
  border: 1.5px solid #bfdbfe;
  box-shadow: 0 4px 16px rgba(59, 130, 246, 0.08);
  opacity: 0;
  transform: translateY(10px) scale(0.95);
  transition: all 0.27s cubic-bezier(0.22, 1, 0.36, 1);
}

.sc-email-card.sc-visible {
  opacity: 1;
  transform: translateY(0) scale(1);
}

.sc-email-topbar {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 6px 10px;
  background: #eff6ff;
  border-bottom: 1px solid #dbeafe;
  font-size: 9px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #3b82f6;
}

.sc-email-icon {
  font-size: 11px;
}

.sc-email-label {
  color: #3b82f6;
}

.sc-email-subject {
  font-size: 12.5px;
  font-weight: 700;
  color: #111827;
  padding: 8px 10px 2px;
  line-height: 1.3;
}

.sc-email-preview {
  font-size: 11px;
  color: #6b7280;
  padding: 2px 10px 6px;
  line-height: 1.4;
}

.sc-email-cta {
  margin: 0 10px 8px;
  padding: 5px 0;
  background: linear-gradient(135deg, #3b82f6, #6366f1);
  color: white;
  font-size: 10px;
  font-weight: 700;
  text-align: center;
  border-radius: 6px;
  letter-spacing: 0.02em;
}

.sc-phone-email {
  background: white;
  border: 1.5px solid #bfdbfe;
  box-shadow: 0 4px 16px rgba(59, 130, 246, 0.08);
}

.sc-phone-sms {
  background: white;
  border: 1.5px solid #a7f3d0;
  box-shadow: 0 4px 16px rgba(16, 185, 129, 0.08);
}

.sc-phone-header {
  display: flex;
  gap: 4px;
  padding: 6px 10px 2px;
}

.sc-phone-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #93c5fd;
}

.sc-dot-green {
  background: #6ee7b7;
}

.sc-phone-body {
  padding: 4px 10px 10px;
}

.sc-phone-label {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #64748b;
  margin-bottom: 5px;
}

.sc-phone-badge {
  width: 20px;
  height: 20px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
}

.sc-badge-email {
  background: #dbeafe;
}

.sc-badge-sms {
  background: #d1fae5;
}

.sc-phone-msg {
  font-size: 12px;
  color: #1e293b;
  line-height: 1.45;
  font-weight: 500;
}

.sc-sms-bubble {
  background: linear-gradient(135deg, #007aff, #0055d4);
  color: white;
  font-size: 12px;
  font-weight: 600;
  padding: 8px 12px;
  border-radius: 14px 14px 4px 14px;
  line-height: 1.4;
  display: inline-block;
  text-align: left;
}

.sc-sms-meta {
  font-size: 9px;
  color: #94a3b8;
  margin-top: 3px;
  text-align: right;
  font-weight: 500;
}

/* ── Mobile story (hidden on desktop) ── */
.sc-mobile-story {
  display: none;
}

/* ══════════════════════════════════
   SIMULATE INTERACTIVE MODAL
   ══════════════════════════════════ */
.sim-wt {
  width: 100%;
}

.sim-wt-header {
  text-align: center;
  margin-bottom: 28px;
  position: relative;
}

.sim-wt-title {
  font-size: 26px;
  font-weight: 800;
  color: #111827;
  margin: 0 0 8px 0;
}

.sim-wt-subtitle {
  font-size: 15px;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
}

.sim-wt-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}

.sim-wt-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 22px 14px 18px;
  background: #f9fafb;
  border: 2px solid #e5e7eb;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.22, 1, 0.36, 1);
  overflow: hidden;
}

.sim-wt-card:hover:not(:disabled) {
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  border-color: #c7d2fe;
  background: white;
}

.sim-wt-card:active:not(:disabled) {
  transform: translateY(0px) scale(0.97);
}

.sim-wt-card:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.sim-wt-card.sim-wt-done {
  border-color: #86efac;
  background: linear-gradient(145deg, #f0fdf4, #dcfce7);
}

.sim-wt-card.sim-wt-done:hover:not(:disabled) {
  border-color: #4ade80;
  box-shadow: 0 8px 25px rgba(34, 197, 94, 0.15);
}

.sim-wt-card-icon {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
  flex-shrink: 0;
}

.sim-wt-bg-blue {
  background: #dbeafe;
}
.sim-wt-bg-amber {
  background: #fef3c7;
}
.sim-wt-bg-violet {
  background: #ede9fe;
}
.sim-wt-bg-green {
  background: #dcfce7;
}
.sim-wt-bg-red {
  background: #fee2e2;
}
.sim-wt-bg-teal {
  background: #ccfbf1;
}

.sim-wt-card-label {
  font-size: 15px;
  font-weight: 700;
  color: #111827;
}

.sim-wt-card-desc {
  font-size: 12px;
  color: #9ca3af;
  line-height: 1.4;
  text-align: center;
}

.sim-wt-check {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 24px;
  height: 24px;
  background: #22c55e;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  animation: sim-check-pop 0.35s cubic-bezier(0.22, 1, 0.36, 1);
}

@keyframes sim-check-pop {
  0% {
    transform: scale(0);
  }
  60% {
    transform: scale(1.3);
  }
  100% {
    transform: scale(1);
  }
}

.sim-wt-spinner {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 20px;
  height: 20px;
  border: 3px solid #e5e7eb;
  border-top-color: #6366f1;
  border-radius: 50%;
  animation: sim-spin 0.6s linear infinite;
}

@keyframes sim-spin {
  to {
    transform: rotate(360deg);
  }
}

.sim-wt-toast {
  background: #111827;
  color: white;
  padding: 10px 20px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  text-align: center;
  animation: sim-toast-in 0.3s ease;
}

.simtoast-enter-active {
  animation: sim-toast-in 0.3s ease;
}
.simtoast-leave-active {
  animation: sim-toast-in 0.3s ease reverse;
}

@keyframes sim-toast-in {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.sim-wt-progress {
  margin-top: 12px;
  text-align: center;
}

.sim-wt-progress-text {
  font-size: 13px;
  color: #9ca3af;
  font-weight: 500;
}

/* ── Sim Back Button ── */
.sim-wt-back {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  font-size: 22px;
  color: #6b7280;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 8px;
  transition: background 0.15s;
}
.sim-wt-back:hover {
  background: #f3f4f6;
  color: #111827;
}

/* ── Product Picker ── */
.sim-wt-picker {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.sim-wt-search-wrap {
  padding: 0 4px;
}

.sim-wt-search {
  width: 100%;
  padding: 10px 14px;
  font-size: 14px;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  outline: none;
  transition: border-color 0.2s;
  box-sizing: border-box;
}
.sim-wt-search:focus {
  border-color: #6366f1;
}

.sim-wt-product-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  max-height: 360px;
  overflow-y: auto;
  padding: 2px;
}

.sim-wt-product {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 10px 8px;
  background: #f9fafb;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
}
.sim-wt-product:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
  border-color: #c7d2fe;
  background: white;
}
.sim-wt-product:active:not(:disabled) {
  transform: scale(0.97);
}
.sim-wt-product:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.sim-wt-product-img {
  width: 80px;
  height: 80px;
  border-radius: 10px;
  overflow: hidden;
  position: relative;
  background: #f3f4f6;
  flex-shrink: 0;
}
.sim-wt-product-img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.sim-wt-product-noimg {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  color: #d1d5db;
}

.sim-wt-product-spin {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
}
.sim-wt-product-spin::after {
  content: '';
  width: 24px;
  height: 24px;
  border: 3px solid #e5e7eb;
  border-top-color: #6366f1;
  border-radius: 50%;
  animation: sim-spin 0.6s linear infinite;
}

.sim-wt-product-name {
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  text-align: center;
  line-height: 1.3;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.sim-wt-product-price {
  font-size: 11px;
  color: #6b7280;
  font-weight: 600;
}

.sim-wt-product-loading {
  border-color: #a5b4fc;
  background: #eef2ff;
}

.sim-wt-empty {
  grid-column: 1 / -1;
  text-align: center;
  color: #9ca3af;
  font-size: 14px;
  padding: 32px 0;
}

/* ── Search View ── */
.sim-wt-search-view {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 20px 0;
}

.sim-wt-search-big-wrap {
  width: 100%;
  padding: 0 20px;
  box-sizing: border-box;
}

.sim-wt-search-big {
  width: 100%;
  padding: 16px 20px;
  font-size: 18px;
  border: 2px solid #e5e7eb;
  border-radius: 14px;
  outline: none;
  transition: border-color 0.2s;
  box-sizing: border-box;
}
.sim-wt-search-big:focus {
  border-color: #6366f1;
}

.sim-wt-search-submit {
  padding: 12px 32px;
  font-size: 16px;
  font-weight: 700;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: white;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 160px;
}
.sim-wt-search-submit:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(99, 102, 241, 0.3);
}
.sim-wt-search-submit:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ── Form View ── */
.sim-wt-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 4px 0;
}

.sim-wt-form-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: left;
}

.sim-wt-form-label {
  font-size: 14px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.3;
}

.sim-wt-form-input {
  width: 100%;
  padding: 12px 14px;
  font-size: 14px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  background: #f9fafb;
  color: #111827;
  outline: none;
  transition: all 0.2s ease;
  font-family: inherit;
}

.sim-wt-form-input:focus {
  border-color: #818cf8;
  background: white;
  box-shadow: 0 0 0 3px rgba(129, 140, 248, 0.15);
}

.sim-wt-form-input::placeholder {
  color: #9ca3af;
}

.sim-wt-form-submit {
  margin-top: 4px;
  padding: 14px 24px;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: white;
  font-size: 15px;
  font-weight: 700;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.sim-wt-form-submit:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(99, 102, 241, 0.3);
}

.sim-wt-form-submit:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ══════════════════════════════════
   KEY HINTS ON BUTTONS
   ══════════════════════════════════ */
.key-hint {
  display: inline-block;
  font-family: inherit;
  font-size: 9px;
  font-weight: 700;
  background: rgba(0, 0, 0, 0.08);
  color: #9ca3af;
  padding: 2px 5px;
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  margin-left: 6px;
  vertical-align: middle;
  line-height: 1;
  letter-spacing: 0;
}

.tooltip-btn-next .key-hint {
  background: rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.7);
  border-color: rgba(255, 255, 255, 0.15);
}

.tooltip-btn-back .key-hint {
  margin-left: 4px;
}

/* ══════════════════════════════════
   CELEBRATION OVERLAY
   ══════════════════════════════════ */
.celebration-overlay {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  overflow-y: auto;
  padding: 24px 16px;
  background: rgba(0, 0, 0, 0.68);
  backdrop-filter: blur(8px);
}

.celebration-card {
  background: #ffffff;
  border-radius: 24px;
  padding: 0 0 36px;
  text-align: center;
  max-width: 480px;
  width: calc(100vw - 32px);
  margin: auto;
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(0, 0, 0, 0.04);
  animation: celebrate-card-in 0.45s cubic-bezier(0.34, 1.4, 0.64, 1);
  position: relative;
  z-index: 1;
  overflow: hidden;
}

.celebration-card-immersive {
  max-width: 580px;
  background: #ffffff;
  padding: 0;
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.18), 0 0 0 1px rgba(0, 0, 0, 0.04);
}

.celebration-card-demo-shell {
  max-width: min(1000px, calc(100vw - 24px));
  width: 100%;
  padding: 0;
  border-radius: 24px;
  background: transparent;
  box-shadow: none;
}

/* ── Demo mode styles (kept for DemoCelebrationV3) ── */
.celebration-demo-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  color: #4f46e5;
  background: rgba(99, 102, 241, 0.1);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 999px;
  padding: 6px 10px;
  margin-bottom: 14px;
}

.celebration-demo-title {
  margin: 0;
  font-size: 38px;
  line-height: 1.08;
  font-weight: 900;
  letter-spacing: -0.02em;
  color: #0f172a;
}

.celebration-demo-subtitle {
  margin: 14px 0 18px 0;
  font-size: 16px;
  line-height: 1.55;
  color: #475569;
  max-width: 58ch;
}

.celebration-demo-highlights {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
  margin-bottom: 20px;
}

.celebration-demo-item {
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 10px 12px;
}

.celebration-btn-shopify {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border: none;
  border-radius: 16px;
  padding: 18px 22px;
  font-size: 20px;
  font-weight: 900;
  letter-spacing: -0.01em;
  color: white;
  cursor: pointer;
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  box-shadow: 0 14px 30px rgba(22, 163, 74, 0.35),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);
  transition: transform 0.15s ease, box-shadow 0.2s ease;
}

.celebration-btn-shopify:hover {
  transform: translateY(-2px);
  box-shadow: 0 20px 36px rgba(22, 163, 74, 0.42),
    inset 0 1px 0 rgba(255, 255, 255, 0.35);
}

.celebration-btn-shopify:active {
  transform: scale(0.985);
}

.shopify-icon {
  font-size: 20px;
}

.shopify-arrow {
  font-size: 22px;
}

.celebration-actions-demo {
  margin-top: 14px;
}

@keyframes celebrate-card-in {
  0% {
    opacity: 0;
    transform: scale(0.92) translateY(18px);
  }
  100% {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

/* ══════════════════════════════════
   CONFETTI
   ══════════════════════════════════ */
.confetti-container {
  position: fixed;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
  z-index: 0;
}

.confetti {
  position: absolute;
  top: -10px;
  background: var(--confetti-color);
  border-radius: 2px;
  animation: confetti-fall linear infinite;
}

@keyframes confetti-fall {
  0% {
    top: -10px;
    opacity: 1;
    transform: translateX(0) rotate(0deg);
  }
  100% {
    top: 110vh;
    opacity: 0;
    transform: translateX(80px) rotate(720deg);
  }
}

/* ══════════════════════════════════
   TRANSITIONS
   ══════════════════════════════════ */
.walkthrough-fade-enter-active,
.walkthrough-fade-leave-active {
  transition: opacity 0.3s ease;
}

.walkthrough-fade-enter-from,
.walkthrough-fade-leave-to {
  opacity: 0;
}

.celebrate-fade-enter-active {
  transition: opacity 0.3s ease;
}

.celebrate-fade-leave-active {
  transition: opacity 0.2s ease;
}

.celebrate-fade-enter-from,
.celebrate-fade-leave-to {
  opacity: 0;
}

.tooltip-pop-enter-active {
  transition: opacity 0.25s ease, filter 0.25s ease;
}

.tooltip-pop-leave-active {
  transition: opacity 0.15s ease;
  position: absolute;
  pointer-events: none;
}

.tooltip-pop-enter-from {
  opacity: 0;
  filter: blur(4px);
}

.tooltip-pop-leave-to {
  opacity: 0;
}

/* ══════════════════════════════════
   MOBILE  (≤ 640px)
   ══════════════════════════════════ */
@media (max-width: 640px) {
  /* ── Progress bar: slimmer, sits below the 56px navbar ── */
  .walkthrough-progress-bar {
    padding: 0;
    top: 56px;
  }

  .progress-track {
    height: 3px;
  }

  .progress-info {
    padding: 5px 12px;
    gap: 8px;
  }

  .progress-groups {
    display: none;
  }

  .progress-step-count {
    font-size: 11px;
    margin-left: 0;
  }

  /* ── Tooltip: bottom-pinned sheet ── */
  .walkthrough-tooltip {
    width: auto !important;
    max-width: none !important;
    bottom: 8px !important;
    left: 8px !important;
    right: 8px !important;
    top: auto !important;
    transform: none !important;
    position: fixed !important;
    padding: 20px 18px 16px;
    border-radius: 16px;
    max-height: 45vh;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  .walkthrough-tooltip::before {
    height: 2px;
  }

  /* ── Compact tooltip: stack vertically on mobile ── */
  .walkthrough-tooltip.is-compact {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    white-space: normal;
    padding: 16px;
  }

  .is-compact .tooltip-content {
    flex-direction: column;
    gap: 4px;
    align-items: flex-start;
  }

  .is-compact .tooltip-title {
    white-space: normal;
    font-size: 15px;
  }

  .is-compact .tooltip-body {
    white-space: normal;
    font-size: 13px;
    line-height: 1.4;
  }

  .is-compact .tooltip-actions {
    width: 100%;
  }

  .is-compact .tooltip-counter {
    display: none;
  }

  /* ── Tooltip icon: smaller ── */
  .tooltip-icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    font-size: 20px;
    margin-bottom: 10px;
  }

  .is-center .tooltip-icon {
    width: 48px;
    height: 48px;
    border-radius: 14px;
    font-size: 24px;
    margin-bottom: 12px;
  }

  /* ── Tooltip text: tighter ── */
  .tooltip-title {
    font-size: 18px;
    margin-bottom: 4px;
  }

  .tooltip-body {
    font-size: 13px;
    line-height: 1.5;
  }

  .tooltip-counter {
    margin-top: 6px;
    font-size: 10px;
  }

  /* ── Tooltip actions: touch-friendly ── */
  .tooltip-actions {
    margin-top: 14px;
    padding-top: 12px;
    gap: 6px;
    flex-wrap: wrap;
  }

  .tooltip-btn {
    font-size: 14px;
    border-radius: 10px;
    -webkit-tap-highlight-color: transparent;
  }

  .tooltip-btn-next {
    padding: 12px 20px;
    font-size: 15px;
    flex: 1;
    text-align: center;
    min-height: 44px;
  }

  .tooltip-btn-back {
    padding: 12px 14px;
    min-height: 44px;
    flex-shrink: 0;
  }

  .tooltip-btn-skip {
    font-size: 12px;
    padding: 8px;
    min-height: 44px;
    display: flex;
    align-items: center;
  }

  .key-hint {
    display: none;
  }

  /* ── Drag objective ── */
  .drag-objective {
    margin-top: 10px;
    padding: 8px 10px;
  }

  .objective-text {
    font-size: 12px;
  }

  /* ── Showcase tooltip: full-width bottom sheet ── */
  .walkthrough-tooltip.is-showcase {
    width: auto !important;
    max-width: none !important;
    max-height: 80vh;
    padding: 20px 16px 16px;
    border-radius: 18px 18px 16px 16px;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    /* Center modals (showcases) stay centered */
    bottom: auto !important;
    left: 50% !important;
    right: auto !important;
    top: 50% !important;
    transform: translate(-50%, -50%) !important;
    width: calc(100vw - 24px) !important;
  }

  /* Hide desktop pipeline, show mobile story */
  .sc-pipeline {
    display: none !important;
  }

  .sc-mobile-story {
    display: block !important;
  }

  .sc-particles {
    display: none;
  }

  .sc-hero-title {
    font-size: 18px;
  }

  .sc-hero-sub {
    font-size: 12px;
    max-width: 100%;
    margin-bottom: 16px;
  }

  .sc-badge {
    font-size: 9px;
    padding: 4px 10px;
    margin-bottom: 8px;
  }

  .sc-hero {
    margin-bottom: 16px;
  }

  /* ── Mobile story vertical timeline ── */
  .sc-m-step {
    display: flex;
    gap: 10px;
    align-items: stretch;
    opacity: 0;
    transform: translateX(-16px);
    transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
  }

  .sc-m-step.sc-visible {
    opacity: 1;
    transform: translateX(0);
  }

  .sc-m-line {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 18px;
    flex-shrink: 0;
    position: relative;
  }

  .sc-m-line::after {
    content: '';
    position: absolute;
    top: 22px;
    bottom: 0;
    width: 2px;
    background: linear-gradient(to bottom, #c7d2fe, #e5e7eb);
    border-radius: 1px;
  }

  .sc-m-line-last::after {
    display: none;
  }

  .sc-m-dot {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    flex-shrink: 0;
    position: relative;
    z-index: 1;
  }

  .sc-m-dot.sc-dot-blue {
    background: linear-gradient(135deg, #3b82f6, #6366f1);
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.35);
  }
  .sc-m-dot.sc-dot-purple {
    background: linear-gradient(135deg, #8b5cf6, #a855f7);
    box-shadow: 0 2px 8px rgba(139, 92, 246, 0.35);
  }
  .sc-m-dot.sc-dot-green {
    background: linear-gradient(135deg, #10b981, #34d399);
    box-shadow: 0 2px 8px rgba(16, 185, 129, 0.35);
  }

  .sc-m-card {
    flex: 1;
    border-radius: 12px;
    padding: 12px;
    margin-bottom: 8px;
    text-align: left;
  }

  .sc-m-blue {
    background: linear-gradient(160deg, #eff6ff, #dbeafe);
    border: 1.5px solid rgba(59, 130, 246, 0.18);
  }

  .sc-m-purple {
    background: linear-gradient(160deg, #f5f3ff, #ede9fe);
    border: 1.5px solid rgba(139, 92, 246, 0.18);
  }

  .sc-m-green {
    background: linear-gradient(160deg, #ecfdf5, #d1fae5);
    border: 1.5px solid rgba(16, 185, 129, 0.18);
  }

  .sc-m-top {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 6px;
  }

  .sc-m-num {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.08);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    font-weight: 800;
    color: #374151;
  }

  .sc-m-icon {
    font-size: 16px;
    line-height: 1;
  }

  .sc-m-title {
    font-size: 13px;
    font-weight: 800;
    color: #0f172a;
  }

  .sc-m-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 3px;
  }

  .sc-m-chips span {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 9px;
    font-weight: 600;
    padding: 2px 6px;
    border-radius: 100px;
    background: rgba(59, 130, 246, 0.08);
    color: #374151;
  }

  .sc-m-chips .sc-act-dot {
    width: 5px;
    height: 5px;
  }

  .sc-m-desc {
    font-size: 11px;
    color: #64748b;
    margin: 0;
    line-height: 1.4;
  }

  /* ── Mobile message bubbles ── */
  .sc-m-bubbles {
    display: flex;
    flex-direction: column;
    gap: 5px;
  }

  .sc-m-bub {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    padding: 6px 8px;
    border-radius: 10px;
    opacity: 0;
    transform: translateY(8px);
    transition: all 0.24s cubic-bezier(0.22, 1, 0.36, 1);
  }

  .sc-m-bub.sc-visible {
    opacity: 1;
    transform: translateY(0);
  }

  .sc-m-bub-email {
    background: white;
    border: 1px solid #bfdbfe;
  }

  .sc-m-bub-sms {
    background: linear-gradient(135deg, #22c55e, #16a34a);
    color: white;
    border: 1px solid rgba(34, 197, 94, 0.3);
  }

  .sc-m-bub-sms strong {
    color: rgba(255, 255, 255, 0.85);
  }

  .sc-m-bub-sms span {
    color: white;
  }

  .sc-m-bub-icon {
    font-size: 14px;
    flex-shrink: 0;
    line-height: 1;
    margin-top: 1px;
  }

  .sc-m-bub-body {
    display: flex;
    flex-direction: column;
    gap: 1px;
    min-width: 0;
  }

  .sc-m-bub-body strong {
    font-size: 8px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #64748b;
  }

  .sc-m-bub-body span {
    font-size: 10px;
    color: #1e293b;
    line-height: 1.3;
  }

  .sc-m-cta-pill {
    display: inline-block;
    background: linear-gradient(135deg, #3b82f6, #6366f1);
    color: white;
    font-size: 8px;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 4px;
    margin-top: 2px;
    width: fit-content;
  }

  /* ── Integrations showcase mobile ── */
  .ig-title {
    font-size: 20px;
  }
  .ig-subtitle {
    font-size: 14px;
  }
  .ig-service {
    padding: 14px 16px;
    gap: 14px;
  }
  .ig-service-logo {
    width: 40px;
    height: 40px;
  }
  .ig-logo-img {
    width: 26px;
    height: 26px;
  }
  .ig-service-name {
    font-size: 16px;
  }
  .ig-service-desc {
    font-size: 13px;
  }
  .ig-note {
    font-size: 13px;
  }

  /* ── Forms (Lead Capture) showcase mobile ── */
  .lf-badge {
    font-size: 10px;
    padding: 3px 10px 3px 7px;
    margin-bottom: 10px;
  }
  .lf-title {
    font-size: 20px;
  }
  .lf-subtitle {
    font-size: 12px;
    max-width: 100%;
  }
  .lf-hero {
    margin-bottom: 14px;
  }
  .lf-tabs {
    margin-bottom: 12px;
  }
  .lf-tab {
    padding: 6px 12px;
    font-size: 11.5px;
    gap: 4px;
  }
  .lf-tab svg {
    width: 13px;
    height: 13px;
  }
  .lf-bar-icons {
    display: none;
  }
  .lf-viewport {
    min-height: 160px;
  }
  .lf-sf-hero-banner {
    padding: 8px 12px;
  }
  .lf-sf-grid {
    gap: 8px;
    padding: 0 12px 14px;
    grid-template-columns: repeat(3, 1fr);
  }
  .lf-sf-card-hide {
    display: none !important;
  }
  .lf-mock-popup {
    width: 150px;
  }
  .lf-mock-popup .lf-mock-body {
    padding: 12px 10px 10px;
  }
  .lf-mock-slide {
    width: 140px;
  }
  .lf-slide-header {
    padding: 8px 10px;
  }
  .lf-slide-avatar {
    width: 22px;
    height: 22px;
    font-size: 12px;
  }
  .lf-slide-body {
    padding: 10px;
  }
  .lf-slide-bubble {
    font-size: 9px;
    padding: 6px 8px;
  }
  .lf-mock-h {
    font-size: 11px;
  }
  .lf-mock-p {
    font-size: 8px;
  }
  .lf-mock-field {
    padding: 5px 8px;
  }
  .lf-mock-field span {
    font-size: 8.5px;
  }
  .lf-mock-cta {
    font-size: 9px;
    padding: 5px 10px;
  }
  .lf-features {
    gap: 4px;
  }
  .lf-chip {
    padding: 4px 10px;
    font-size: 9.5px;
    gap: 4px;
  }
  .lf-chip-dot {
    width: 5px;
    height: 5px;
  }
  .lf-generating {
    font-size: 12px;
    padding: 10px 14px;
  }

  /* ── Welcome showcase mobile ── */
  .welcome-showcase {
    text-align: center;
  }

  .wc-check-svg {
    width: 44px;
    height: 44px;
  }

  .wc-check-wrap {
    margin-bottom: 12px;
  }

  .wc-demo-badge {
    font-size: 9px;
    padding: 3px 10px;
    margin-bottom: 10px;
  }

  .wc-title {
    font-size: 20px;
    margin-bottom: 6px;
  }

  .wc-subtitle {
    font-size: 13px;
    margin-bottom: 16px;
  }

  .wc-pipeline {
    margin-bottom: 14px;
    padding: 8px 0;
    flex-wrap: wrap;
    gap: 4px;
    justify-content: center;
  }

  .wc-step {
    padding: 6px 10px;
    border-radius: 8px;
  }

  .wc-step-emoji {
    font-size: 14px;
  }

  .wc-step-name {
    font-size: 11px;
  }

  .wc-connector {
    width: 18px;
  }

  .wc-meta {
    gap: 4px;
    flex-wrap: wrap;
    justify-content: center;
  }

  .wc-meta-pill {
    font-size: 10px;
    padding: 3px 8px;
  }

  /* ── Simulate interactive mobile ── */
  .sim-wt-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }

  .sim-wt-card {
    padding: 14px 8px 12px;
  }

  .sim-wt-card-icon {
    width: 36px;
    height: 36px;
    font-size: 18px;
    border-radius: 8px;
  }

  .sim-wt-card-label {
    font-size: 12px;
  }

  .sim-wt-card-desc {
    display: none;
  }

  .sim-wt-title {
    font-size: 18px;
  }

  .sim-wt-sub {
    font-size: 12px;
  }

  /* ── Celebration ── */
  .celebration-overlay {
    padding: 12px 8px;
  }

  .celebration-card {
    padding: 0;
    border-radius: 20px;
  }

  .celebration-card-demo-shell {
    max-width: calc(100vw - 16px);
    padding: 0;
    border-radius: 18px;
  }
}

/* ── OFF-TRACK RETURN PILL ── */
.offtrack-pill {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10005;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px 10px 16px;
  background: white;
  border-radius: 40px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(99, 102, 241, 0.15);
  font-size: 13px;
  color: #334155;
  white-space: nowrap;
}

.offtrack-icon {
  font-size: 16px;
  line-height: 1;
}

.offtrack-text {
  font-weight: 500;
  color: #64748b;
}

.offtrack-btn {
  padding: 6px 14px;
  border: none;
  border-radius: 20px;
  background: linear-gradient(135deg, #6366f1, #4f46e5);
  color: white;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.15s;
}

.offtrack-btn:hover {
  transform: scale(1.03);
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.4);
}

.offtrack-dismiss {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #94a3b8;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  flex-shrink: 0;
}

.offtrack-dismiss:hover {
  background: #f1f5f9;
  color: #475569;
}

/* Slide transition */
.offtrack-slide-enter-active {
  transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1),
    opacity 0.25s ease;
}
.offtrack-slide-leave-active {
  transition: transform 0.2s ease, opacity 0.15s ease;
}
.offtrack-slide-enter-from {
  transform: translateX(-50%) translateY(20px);
  opacity: 0;
}
.offtrack-slide-leave-to {
  transform: translateX(-50%) translateY(20px);
  opacity: 0;
}

@media (max-width: 600px) {
  .offtrack-pill {
    left: 8px;
    right: 8px;
    transform: none;
    border-radius: 16px;
    flex-wrap: wrap;
    justify-content: center;
    gap: 6px;
    padding: 12px 14px;
    white-space: normal;
    text-align: center;
  }
  .offtrack-text {
    font-size: 12px;
  }
  .offtrack-btn {
    padding: 10px 18px;
    font-size: 14px;
    min-height: 44px;
    -webkit-tap-highlight-color: transparent;
  }
  .offtrack-dismiss {
    width: 32px;
    height: 32px;
    min-height: 44px;
    min-width: 44px;
  }
  .offtrack-slide-enter-from,
  .offtrack-slide-leave-to {
    transform: translateY(20px);
    opacity: 0;
  }
}
</style>

<!-- Global styles for interactive walkthrough targets (applied to elements outside this component) -->
<style>
.walkthrough-interactive-target {
  position: relative;
  z-index: 10001 !important;
  pointer-events: auto !important;
}

/* Keep the app navbar above the walkthrough overlay so it's never dimmed */
body.walkthrough-active .navbar {
  z-index: 10002 !important;
}

/* ══════════════════════════════════
   GENERATING USER OVERLAY
   ══════════════════════════════════ */
.wt-generating-overlay {
  position: fixed;
  inset: 0;
  z-index: 10010;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}

.wt-generating-card {
  background: #fff;
  border-radius: 20px;
  padding: 40px 36px;
  max-width: 360px;
  width: 90vw;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  animation: wt-gen-pop 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes wt-gen-pop {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.wt-generating-spinner {
  width: 44px;
  height: 44px;
  margin: 0 auto 20px;
  border: 3px solid #e5e7eb;
  border-top-color: #6366f1;
  border-radius: 50%;
  animation: wt-spin 0.8s linear infinite;
}

@keyframes wt-spin {
  to {
    transform: rotate(360deg);
  }
}

.wt-generating-title {
  font-size: 18px;
  font-weight: 650;
  color: #1a1a2e;
  margin: 0 0 8px;
}

.wt-generating-body {
  font-size: 14px;
  color: #6b7280;
  line-height: 1.5;
  margin: 0;
}
</style>
